<template>
  <ValidationObserver ref="observer">
    <div
      v-if="loadMenu"
      :class="{ 'menu-page-scroll': !embedded }"
      class="ai-menu-config-root"
    >
      <!-- Top bar: Gemini-style chat trigger -->
      <div class="ai-menu-config-top-bar">
        <b-button
          type="button"
          variant="link"
          class="ai-menu-gemini-btn p-0"
          v-b-tooltip.hover.noninteractive="'Open AI chat (optional)'"
          aria-label="Open Google Gemini-style assistant chat"
          @click="aiChatSidebarOpen = true"
        >
          <svg
            class="gemini-brand-svg"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="34"
            height="34"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="geminiGradA" x1="12%" y1="100%" x2="88%" y2="0%">
                <stop offset="0%" stop-color="#4285F4" />
                <stop offset="33%" stop-color="#8E5AF7" />
                <stop offset="66%" stop-color="#E871B5" />
                <stop offset="100%" stop-color="#F9AB00" />
              </linearGradient>
            </defs>
            <g transform="translate(12,12)">
              <ellipse rx="8.2" ry="3.35" fill="url(#geminiGradA)" transform="rotate(45)" />
              <ellipse
                rx="8.2"
                ry="3.35"
                fill="url(#geminiGradA)"
                transform="rotate(-45)"
                opacity="0.92"
              />
            </g>
          </svg>
        </b-button>
      </div>

      <b-sidebar
        id="ai-menu-llm-sidebar"
        v-model="aiChatSidebarOpen"
        title="AI assistant"
        right
        shadow
        backdrop
        width="420px"
      >
        <div class="ai-menu-llm-sidebar-body">
          <LlmChat embedded-panel />
        </div>
      </b-sidebar>

      <!-- =====================================================================
           SQL entry: one or more DataSQLs → Generate UI (single LLM call).
           Single SQL  → Screen = Screenlet with one Vizlet.
           Multiple   → Screen = Screenlet with N Vizlets (one per SQL).
           ===================================================================== -->
      <b-card class="shadow-sm ai-sql-entry-card mb-3">
        <div class="d-flex align-items-center justify-content-between mb-2">
          <div>
            <h6 class="mb-1">DataSQLs</h6>
            <p class="text-muted small mb-0">
              Enter one DataSQL per visualization. The LLM will auto-populate
              <strong>Screen</strong>, <strong>Screenlet</strong> and each
              <strong>Vizlet</strong> below.
            </p>
          </div>
          <div class="ai-sql-entry-actions">
            <b-button
              size="sm"
              variant="outline-secondary"
              @click="addDataSql"
              :disabled="generateUiLoading"
            >+ Add SQL</b-button>
            <!-- DEV-ONLY: loads a bundled fixture when the LLM is unreachable. -->
            <b-button
              size="sm"
              variant="outline-info"
              class="ml-2 btn-load-sample"
              :disabled="generateUiLoading"
              v-b-tooltip.hover.noninteractive="'Dev fallback — load bundled sample JSON'"
              @click="loadSampleUi"
            >Load sample</b-button>
            <b-button
              variant="primary"
              size="sm"
              class="ml-2 btn-generate-ui"
              :disabled="!canGenerateUi || generateUiLoading"
              @click="generateUi"
            >
              <span v-if="generateUiLoading">Generating UI…</span>
              <span v-else>Generate UI</span>
            </b-button>
          </div>
        </div>
        <div
          v-for="(q, i) in datasqls"
          :key="'datasql-' + i"
          class="ai-sql-row"
        >
          <label class="ai-sql-row-label">SQL {{ i + 1 }}</label>
          <b-form-textarea
            v-model="datasqls[i]"
            rows="2"
            max-rows="6"
            class="ai-sql-row-textarea"
            placeholder="SELECT … FROM … (one per vizlet)"
          />
          <b-button
            v-if="datasqls.length > 1"
            variant="outline-danger"
            size="sm"
            class="ai-sql-row-remove"
            @click="removeDataSql(i)"
            :disabled="generateUiLoading"
            aria-label="Remove SQL"
          >×</b-button>
        </div>

        <b-row class="mt-2">
          <b-col md="12">
            <b-form-group
              label="What to show (user prompt for LLM)"
              label-class="small text-muted mb-1"
              label-align="left"
            >
              <b-form-textarea
                v-model="generateUiUserPrompt"
                rows="2"
                max-rows="5"
                class="ai-sql-row-textarea"
                placeholder='e.g. Show me a multiline chart of service type as different lines, branch code as x axis and ticket count as values'
                :disabled="generateUiLoading"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <div v-if="generateUiError" class="text-danger small mt-2">{{ generateUiError }}</div>
      </b-card>

      <!-- =====================================================================
           3-Level Progress Stepper
           ===================================================================== -->
      <div class="ai-step-progress" role="tablist">
        <button
          v-for="s in steps"
          :key="s.id"
          type="button"
          class="ai-step"
          :class="{
            'ai-step-active': currentStep === s.id,
            'ai-step-done': currentStep > s.id,
            'ai-step-disabled': !isStepReachable(s.id),
          }"
          :disabled="!isStepReachable(s.id)"
          @click="gotoStep(s.id)"
        >
          <span class="ai-step-num">{{ s.id }}</span>
          <span class="ai-step-label">{{ s.label }}</span>
        </button>
      </div>

      <!-- =====================================================================
           Workspace: left tree + right contextual form
           ===================================================================== -->
      <div class="ai-workspace">
        <!-- LEFT: Tree -->
        <aside class="ai-tree-panel">
          <div class="ai-tree-header">
            <span class="ai-tree-root-label">{{ parentMenuDisplay || 'My Dashboard' }}</span>
            <b-button
              size="sm"
              variant="outline-primary"
              class="ai-tree-add-root"
              @click="addScreen"
              v-b-tooltip.hover.noninteractive="'Add Screen'"
            >+ Screen</b-button>
          </div>
          <ul v-if="aiConfig.screens.length" class="ai-tree">
            <li
              v-for="(screen, si) in aiConfig.screens"
              :key="'sc-' + si"
              class="ai-tree-item ai-tree-screen"
            >
              <div
                class="ai-tree-node"
                :class="{ 'ai-tree-node-selected': isSelected('screen', si) }"
                @click="selectNode({ type: 'screen', si })"
              >
                <span
                  class="ai-tree-caret"
                  @click.stop="toggleExpand('sc-' + si)"
                  role="button"
                  aria-label="Toggle"
                >{{ expanded['sc-' + si] ? '▾' : '▸' }}</span>
                <span class="ai-tree-node-label">
                  {{ screen.screen_title || 'Screen ' + (si + 1) }}
                </span>
                <span class="ai-tree-actions">
                  <b-button
                    size="sm"
                    variant="link"
                    class="ai-tree-action-btn"
                    v-b-tooltip.hover.noninteractive="'Add Screenlet'"
                    @click.stop="addScreenlet(si)"
                  >+</b-button>
                  <b-button
                    size="sm"
                    variant="link"
                    class="ai-tree-action-btn ai-tree-remove-btn"
                    v-b-tooltip.hover.noninteractive="'Remove Screen'"
                    @click.stop="removeScreen(si)"
                  >×</b-button>
                </span>
              </div>
              <ul v-show="expanded['sc-' + si]" class="ai-tree-children">
                <template v-if="mergeScreenScreenlet(si)">
                  <li
                    v-for="(vz, vi) in screen.screenlets[0].vizlets"
                    :key="'vz-' + si + '-0-' + vi"
                    class="ai-tree-item ai-tree-vizlet"
                  >
                    <div
                      class="ai-tree-node"
                      :class="{
                        'ai-tree-node-selected': isSelected('vizlet', si, 0, vi),
                      }"
                      @click="selectNode({ type: 'vizlet', si, sli: 0, vi })"
                    >
                      <span class="ai-tree-caret-placeholder">•</span>
                      <span class="ai-tree-node-label">
                        {{ vz.vizlet_title || 'Vizlet ' + (vi + 1) }}
                      </span>
                      <span class="ai-tree-actions">
                        <b-button
                          size="sm"
                          variant="link"
                          class="ai-tree-action-btn ai-tree-remove-btn"
                          v-b-tooltip.hover.noninteractive="'Remove Vizlet'"
                          @click.stop="removeVizlet(si, 0, vi)"
                        >×</b-button>
                      </span>
                    </div>
                  </li>
                  <li class="ai-tree-inline-add" @click="addVizlet(si, 0)">
                    + Add Vizlet
                  </li>
                  <li class="ai-tree-inline-add" @click="addScreenlet(si)">
                    + Add Screenlet
                  </li>
                </template>
                <template v-else>
                  <li
                    v-for="(sl, sli) in screen.screenlets"
                    :key="'sl-' + si + '-' + sli"
                    class="ai-tree-item ai-tree-screenlet"
                  >
                    <div
                      class="ai-tree-node"
                      :class="{ 'ai-tree-node-selected': isSelected('screenlet', si, sli) }"
                      @click="selectNode({ type: 'screenlet', si, sli })"
                    >
                      <span
                        class="ai-tree-caret"
                        @click.stop="toggleExpand('sl-' + si + '-' + sli)"
                        role="button"
                        aria-label="Toggle"
                      >{{ expanded['sl-' + si + '-' + sli] ? '▾' : '▸' }}</span>
                      <span class="ai-tree-node-label">
                        {{ sl.title || 'Screenlet ' + (sli + 1) }}
                      </span>
                      <span class="ai-tree-actions">
                        <b-button
                          size="sm"
                          variant="link"
                          class="ai-tree-action-btn"
                          v-b-tooltip.hover.noninteractive="'Add Vizlet'"
                          @click.stop="addVizlet(si, sli)"
                        >+</b-button>
                        <b-button
                          size="sm"
                          variant="link"
                          class="ai-tree-action-btn ai-tree-remove-btn"
                          v-b-tooltip.hover.noninteractive="'Remove Screenlet'"
                          @click.stop="removeScreenlet(si, sli)"
                        >×</b-button>
                      </span>
                    </div>
                    <ul v-show="expanded['sl-' + si + '-' + sli]" class="ai-tree-children">
                      <li
                        v-for="(vz, vi) in sl.vizlets"
                        :key="'vz-' + si + '-' + sli + '-' + vi"
                        class="ai-tree-item ai-tree-vizlet"
                      >
                        <div
                          class="ai-tree-node"
                          :class="{
                            'ai-tree-node-selected': isSelected('vizlet', si, sli, vi),
                          }"
                          @click="selectNode({ type: 'vizlet', si, sli, vi })"
                        >
                          <span class="ai-tree-caret-placeholder">•</span>
                          <span class="ai-tree-node-label">
                            {{ vz.vizlet_title || 'Vizlet ' + (vi + 1) }}
                          </span>
                          <span class="ai-tree-actions">
                            <b-button
                              size="sm"
                              variant="link"
                              class="ai-tree-action-btn ai-tree-remove-btn"
                              v-b-tooltip.hover.noninteractive="'Remove Vizlet'"
                              @click.stop="removeVizlet(si, sli, vi)"
                            >×</b-button>
                          </span>
                        </div>
                      </li>
                      <li class="ai-tree-inline-add" @click="addVizlet(si, sli)">
                        + Add Vizlet
                      </li>
                    </ul>
                  </li>
                  <li class="ai-tree-inline-add" @click="addScreenlet(si)">
                    + Add Screenlet
                  </li>
                </template>
              </ul>
            </li>
          </ul>
          <div v-else class="ai-tree-empty text-muted small">
            No configuration yet. Enter a DataSQL above and click
            <strong>Generate UI</strong>, or use <strong>+ Screen</strong>.
          </div>
        </aside>

        <!-- RIGHT: Contextual form -->
        <section class="ai-form-panel">
          <div v-if="!hasSelection" class="ai-form-empty text-muted">
            <p class="mb-0">
              Select a <strong>Screen</strong>, <strong>Screenlet</strong>, or
              <strong>Vizlet</strong> on the left to configure it here.
            </p>
          </div>

          <!-- =================== STEP 1: SCREEN FORM (optionally merged with single screenlet) =================== -->
          <div v-else-if="showScreenLevelForm" class="ai-form">
            <div class="ai-form-header">
              <h5 class="mb-0">
                {{ mergeScreenScreenlet(selectedNode.si) ? 'Screen & Screenlet Configuration' : 'Screen Configuration' }}
              </h5>
              <span class="ai-form-breadcrumb text-muted small">
                <template v-if="mergeScreenScreenlet(selectedNode.si) && mergedScreenletForForm">
                  {{ parentMenuDisplay }} /
                  {{ selectedScreen.screen_title || 'Screen' }} /
                  {{ mergedScreenletForForm.title || 'Screenlet' }}
                </template>
                <template v-else>
                  {{ parentMenuDisplay }} / {{ selectedScreen.screen_title || 'Screen' }}
                </template>
              </span>
            </div>
            <b-row>
              <b-col md="6">
                <b-form-group label="Screen Title" label-align="left">
                  <b-form-input
                    v-model="selectedScreen.screen_title"
                    placeholder="e.g. Service Ticket Volume"
                  />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Screen Type" label-align="left">
                  <b-form-input v-model="selectedScreen.screen_type" placeholder="ST01" />
                </b-form-group>
              </b-col>
              <b-col md="12">
                <b-form-group
                  label="Listbox SQL"
                  label-align="left"
                  description="Supplies filter options for the screen."
                >
                  <b-form-textarea
                    v-model="selectedScreen.screen_listbox.listbox_sql"
                    rows="2"
                    max-rows="6"
                    placeholder="SELECT value, label FROM …"
                  />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Filter Model" label-align="left">
                  <b-form-input
                    v-model="selectedScreen.screen_listbox.filter_model"
                    placeholder="anywhichway/freeform"
                  />
                </b-form-group>
              </b-col>
<!--              <b-col md="6">
                <b-form-group label="Card Style" label-align="left">
                  <b-form-input
                    v-model="selectedScreen.screen_card_style"
                    placeholder="null or inline CSS"
                  />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Date-Range Filter" label-align="left">
                  <b-form-checkbox v-model="selectedScreen.date_range_filter_display">
                    Show date range
                  </b-form-checkbox>
                </b-form-group>
              </b-col>
              <b-col md="6" v-if="selectedScreen.date_range_filter_display">
                <b-form-group label="Default Range" label-align="left">
                  <b-form-input
                    v-model="selectedScreen.date_range_filter_config.default_range"
                    placeholder="last_30_days"
                  />
                </b-form-group>
                <b-form-checkbox v-model="selectedScreen.date_range_filter_config.allow_custom">
                  Allow custom range
                </b-form-checkbox>
              </b-col>-->
              <b-col md="12">
                <b-form-group label="Filter Display (JSON)" label-align="left">
                  <b-form-textarea
                    :value="jsonPretty(selectedScreen.screen_listbox.filter_display)"
                    @input="onJsonEdit($event, selectedScreen.screen_listbox, 'filter_display', [])"
                    rows="4"
                    class="sql-textarea"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <template v-if="mergeScreenScreenlet(selectedNode.si) && mergedScreenletForForm">
              <hr class="my-4" />
              <h6 class="text-muted text-uppercase small mb-3">Screenlet</h6>
              <b-row>
                <b-col md="6">
                  <b-form-group label="Title" label-align="left">
                    <b-form-input
                      v-model="mergedScreenletForForm.title"
                      placeholder="e.g. Tickets by Service Type"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Short Title" label-align="left">
                    <b-form-input
                      v-model="mergedScreenletForForm.title_short"
                      placeholder="Ticket Volume"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="12">
                  <b-form-group label="Tooltip / Info" label-align="left">
                    <b-form-textarea
                      v-model="mergedScreenletForForm.title_info"
                      rows="2"
                      max-rows="4"
                      placeholder="Short description shown in a tooltip"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group label="Screenlet Type" label-align="left">
                    <b-form-input v-model="mergedScreenletForForm.screenlet_type" placeholder="SL01" />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group label="Sequence" label-align="left">
                    <b-form-input type="number" v-model.number="mergedScreenletForForm.sequence" />
                  </b-form-group>
                </b-col>
<!--                <b-col md="4">
                  <b-form-group label="Card Style" label-align="left">
                    <b-form-input
                      v-model="mergedScreenletForForm.screenlet_card_style"
                      placeholder="null or inline CSS"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group label="Default Count" label-align="left">
                    <b-form-input type="number" v-model.number="mergedScreenletForForm.default_count" />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group label="Max Count" label-align="left">
                    <b-form-input type="number" v-model.number="mergedScreenletForForm.max_count" />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group label="Allow Additions" label-align="left">
                    <b-form-checkbox v-model="mergedScreenletForForm.addition_flag">
                      User may add vizlets
                    </b-form-checkbox>
                  </b-form-group>
                </b-col>-->
                <b-col md="12">
                  <b-form-group label="Screenlet Listbox SQL" label-align="left">
                    <b-form-textarea
                      v-model="mergedScreenletForForm.screenlet_listbox.listbox_sql"
                      rows="2"
                      max-rows="6"
                    />
                  </b-form-group>
                </b-col>
<!--                <b-col md="6">
                  <b-form-group label="Date-Range Filter" label-align="left">
                    <b-form-checkbox v-model="mergedScreenletForForm.date_range_filter_display">
                      Show date range
                    </b-form-checkbox>
                  </b-form-group>
                </b-col>
                <b-col md="6" v-if="mergedScreenletForForm.date_range_filter_display">
                  <b-form-group label="Default Range" label-align="left">
                    <b-form-input
                      v-model="mergedScreenletForForm.date_range_filter_config.default_range"
                    />
                  </b-form-group>
                  <b-form-checkbox v-model="mergedScreenletForForm.date_range_filter_config.allow_custom">
                    Allow custom range
                  </b-form-checkbox>
                </b-col>-->
              </b-row>
            </template>
          </div>

          <!-- =================== STEP 2: SCREENLET FORM =================== -->
          <div
            v-else-if="selectedNode.type === 'screenlet' && selectedScreenlet && !mergeScreenScreenlet(selectedNode.si)"
            class="ai-form"
          >
            <div class="ai-form-header">
              <h5 class="mb-0">Screenlet Configuration</h5>
              <span class="ai-form-breadcrumb text-muted small">
                {{ parentMenuDisplay }} /
                {{ (selectedScreen && selectedScreen.screen_title) || 'Screen' }} /
                {{ selectedScreenlet.title || 'Screenlet' }}
              </span>
            </div>
            <b-row>
              <b-col md="6">
                <b-form-group label="Title" label-align="left">
                  <b-form-input v-model="selectedScreenlet.title" placeholder="e.g. Tickets by Service Type" />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Short Title" label-align="left">
                  <b-form-input v-model="selectedScreenlet.title_short" placeholder="Ticket Volume" />
                </b-form-group>
              </b-col>
              <b-col md="12">
                <b-form-group label="Tooltip / Info" label-align="left">
                  <b-form-textarea
                    v-model="selectedScreenlet.title_info"
                    rows="2"
                    max-rows="4"
                    placeholder="Short description shown in a tooltip"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group label="Screenlet Type" label-align="left">
                  <b-form-input v-model="selectedScreenlet.screenlet_type" placeholder="SL01" />
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group label="Sequence" label-align="left">
                  <b-form-input type="number" v-model.number="selectedScreenlet.sequence" />
                </b-form-group>
              </b-col>
<!--              <b-col md="4">
                <b-form-group label="Card Style" label-align="left">
                  <b-form-input v-model="selectedScreenlet.screenlet_card_style" placeholder="null or inline CSS" />
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group label="Default Count" label-align="left">
                  <b-form-input type="number" v-model.number="selectedScreenlet.default_count" />
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group label="Max Count" label-align="left">
                  <b-form-input type="number" v-model.number="selectedScreenlet.max_count" />
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group label="Allow Additions" label-align="left">
                  <b-form-checkbox v-model="selectedScreenlet.addition_flag">
                    User may add vizlets
                  </b-form-checkbox>
                </b-form-group>
              </b-col>-->
              <b-col md="12">
                <b-form-group label="Screenlet Listbox SQL" label-align="left">
                  <b-form-textarea
                    v-model="selectedScreenlet.screenlet_listbox.listbox_sql"
                    rows="2"
                    max-rows="6"
                  />
                </b-form-group>
              </b-col>
<!--              <b-col md="6">
                <b-form-group label="Date-Range Filter" label-align="left">
                  <b-form-checkbox v-model="selectedScreenlet.date_range_filter_display">
                    Show date range
                  </b-form-checkbox>
                </b-form-group>
              </b-col>
              <b-col md="6" v-if="selectedScreenlet.date_range_filter_display">
                <b-form-group label="Default Range" label-align="left">
                  <b-form-input
                    v-model="selectedScreenlet.date_range_filter_config.default_range"
                  />
                </b-form-group>
                <b-form-checkbox v-model="selectedScreenlet.date_range_filter_config.allow_custom">
                  Allow custom range
                </b-form-checkbox>
              </b-col>-->
            </b-row>
          </div>

          <!-- =================== STEP 3: VIZLET FORM (accordion) =================== -->
          <div
            v-else-if="selectedNode.type === 'vizlet' && selectedVizlet"
            class="ai-form ai-vizlet-form"
          >
            <div class="ai-form-header">
              <h5 class="mb-0">Vizlet Configuration</h5>
              <span class="ai-form-breadcrumb text-muted small">
                {{ parentMenuDisplay }} /
                {{ (selectedScreen && selectedScreen.screen_title) || 'Screen' }} /
                {{ (selectedScreenlet && selectedScreenlet.title) || 'Screenlet' }} /
                {{ selectedVizlet.vizlet_title || 'Vizlet' }}
              </span>
            </div>

            <!-- ======== Chart preview & DataSQL (above Metrics & Mapping) ======== -->
            <b-card class="preview-card shadow-sm mb-3">
              <div class="preview-charts" style="max-width: 700px;">
                <!--                &lt;!&ndash; LEFT: Existing Chart &ndash;&gt;
                                <div class="chart-box">
                                  <h6 class="text-muted">Existing Chart</h6>
                                  <div :id="existingChartElId" ref="r1c1" class="scrollmenu"></div>
                                </div>-->
                <!-- RIGHT: Preview Chart -->
                <div class="chart-box">
                  <div class="preview-chart-header">
                    <h6 class="text-muted mb-0">Preview Chart</h6>
                    <div class="preview-viz-select-wrap">
                      <label class="preview-viz-label" for="ai-preview-viz-select">Visualization</label>
                      <b-form-select
                          id="ai-preview-viz-select"
                          v-model="selectedPreviewVizType"
                          :options="previewVizTypeOptions"
                          size="sm"
                          class="preview-viz-select-control"
                      />
                    </div>
                  </div>
                  <div class="preview-chart-mount">
                    <div
                        v-show="previewLoading"
                        class="preview-chart-loading text-center p-4"
                    >
                      Loading preview…
                    </div>
                    <div
                        :id="previewChartElId"
                        ref="r1c2"
                        class="scrollmenu preview-chart-canvas"
                        :class="{ 'preview-chart-canvas-dim': previewLoading }"
                    ></div>
                  </div>
                </div>
              </div>

              <b-row class="preview-sql-row">
                <!--                <b-col md="6" class="preview-col-wrap">
                                  <b-form-group label="DataSQL (saved menu)" label-align="left" class="sql-form-group">
                                    <b-form-textarea
                                      :value="cleanedDataSql"
                                      disabled
                                      auto-grow
                                      :no-resize="false"
                                      class="sql-textarea"
                                    />
                                  </b-form-group>
                                </b-col>
                -->
                <b-col md="12" class="preview-col-wrap">
                  <b-form-group label="DataSQL (this vizlet)" label-align="left" class="sql-form-group">
                    <b-form-textarea
                        v-model="dataSqlId"
                        auto-grow
                        :no-resize="false"
                        class="sql-textarea"
                        placeholder="SELECT … (AI suggestions & preview)"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row
                  class="preview-mapping-row pb-3"
                  v-if="
                  (vizParams && Object.keys(vizParams).length) ||
                    (selectedPreviewVizType === 'tableBarChart' &&
                      (previewTableBarRows.length || (dataSqlId || '').trim()))
                "
              >
                <b-col
                    v-if="vizParams && Object.keys(vizParams).length"
                    md="12"
                    class="preview-col-wrap"
                >
                  <!--                  <h6 class="text-muted mb-2">Existing Chart Variable Mapping</h6>-->
                  <div class="mapping-table-wrap">
                    <!--                    <b-table-simple
                                          v-if="existingVizFunction !== 'tableBarChart'"
                                          bordered
                                          small
                                          responsive
                                          class="mapping-table"
                                        >
                                          <b-thead head-variant="light">
                                            <b-tr>
                                              <b-th>Required</b-th>
                                              <b-th>Column</b-th>
                                            </b-tr>
                                          </b-thead>
                                          <b-tbody>
                                            <b-tr
                                              v-for="row in existingMappingRows"
                                              :key="'existing-' + row.axisLabel"
                                            >
                                              <b-td>{{ row.axisLabel }}</b-td>
                                              <b-td><code>{{ row.expression }}</code></b-td>
                                            </b-tr>
                                          </b-tbody>
                                        </b-table-simple>-->

                    <!--                    <b-table-simple v-else bordered small responsive class="mapping-table">-->
<!--                    <b-table-simple bordered small responsive class="mapping-table">
                      <b-thead head-variant="light">
                        <b-tr>
                          <b-th>Required</b-th>
                          <b-th>Column</b-th>
                          <b-th>Type</b-th>
                          <b-th v-if="tableBarHasGraph">Graph</b-th>
                        </b-tr>
                      </b-thead>
                      <b-tbody>
                        <b-tr
                            v-for="row in tableBarExistingMappingRows"
                            :key="'tblbar-' + row.required"
                        >
                          <b-td>{{ row.required }}</b-td>
                          <b-td>{{ row.column }}</b-td>
                          <b-td>{{ row.type }}</b-td>
                          <b-td v-if="tableBarHasGraph">{{ row.graph }}</b-td>
                        </b-tr>
                      </b-tbody>
                    </b-table-simple>-->
                  </div>
                </b-col>
                <b-col
                    v-if="selectedPreviewVizType === 'tableBarChart'"
                    :md="vizParams && Object.keys(vizParams).length ? 6 : 12"
                    class="preview-col-wrap"
                >
                  <h6 class="text-muted mb-2">Preview Chart Variable Mapping</h6>
                  <div class="mapping-table-wrap">
                    <b-table-simple bordered small responsive class="mapping-table">
                      <b-thead head-variant="light">
                        <b-tr>
                          <b-th>Required</b-th>
                          <b-th>Column</b-th>
                          <b-th>Type</b-th>
                          <b-th>Graph</b-th>
                        </b-tr>
                      </b-thead>
                      <b-tbody>
                        <b-tr
                            v-for="row in previewTableBarRows"
                            :key="'tblbar-prev-' + row.required"
                        >
                          <b-td>{{ row.required }}</b-td>
                          <b-td>
                            <input
                                v-model="row.label"
                                class="inline-mapping-input"
                                placeholder="Header label"
                            />
                          </b-td>
                          <b-td>
                            <b-form-select
                                v-model="row.type"
                                :options="tableBarTypeOptions"
                                size="sm"
                                class="type-select"
                            />
                          </b-td>
                          <b-td>
                            <b-form-select
                                v-model="row.graph"
                                :options="tableBarGraphOptions"
                                size="sm"
                                class="type-select"
                            />
                          </b-td>
                        </b-tr>
                      </b-tbody>
                    </b-table-simple>
                    <p
                        v-if="!previewTableBarRows.length"
                        class="text-muted small mb-0 mt-1"
                    >
                      Click <strong>Generate Preview</strong> or
                      <strong>Generate with AI</strong> to load columns from your SQL.
                    </p>
                  </div>
                </b-col>
              </b-row>
              <div class="preview-actions-row">
                <div class="preview-actions-left">
                  <b-button
                      variant="primary"
                      size="sm"
                      class="btn-generate-ai"
                      :disabled="aiGenerateLoading || !dataSqlId.trim()"
                      @click="generateWithAi"
                  >
                    <span v-if="aiGenerateLoading">Generating…</span>
                    <span v-else>Generate with AI</span>
                  </b-button>
                  <span v-if="aiGenerateError" class="text-danger small ai-generate-error">{{
                      aiGenerateError
                    }}</span>
                </div>
                <div class="preview-actions-right">
                  <b-button
                      class="btn-sm"
                      variant="info"
                      :disabled="!isPreviewEnabled"
                      @click="generatePreview"
                  >Generate Preview</b-button>
                </div>
              </div>
            </b-card>

            <!-- Accordion: Basic Info (hidden when this screenlet has only one vizlet) -->
            <b-card v-if="!hideVizletBasicSection" no-body class="ai-acc">
              <b-card-header class="p-0">
                <b-button block v-b-toggle.acc-basic variant="link" class="ai-acc-btn">
                  Basic Info
                </b-button>
              </b-card-header>
              <b-collapse id="acc-basic" visible accordion="vizlet-acc">
                <b-card-body>
                  <b-row>
                    <b-col md="6">
                      <b-form-group label="Vizlet Title" label-align="left">
                        <b-form-input v-model="selectedVizlet.vizlet_title" />
                      </b-form-group>
                    </b-col>
                    <b-col md="6">
                      <b-form-group label="Short Title" label-align="left">
                        <b-form-input v-model="selectedVizlet.vizlet_title_short" />
                      </b-form-group>
                    </b-col>
                    <b-col md="12">
                      <b-form-group label="Vizlet Info" label-align="left">
                        <b-form-textarea v-model="selectedVizlet.vizlet_title_info" rows="2" />
                      </b-form-group>
                    </b-col>
                    <b-col md="6">
                      <b-form-group label="Viz Type (function)" label-align="left">
                        <b-form-select
                          v-model="selectedVizlet.viztype_function_name"
                          :options="previewVizTypeOptions"
                          @change="onVizletFunctionChange"
                        />
                      </b-form-group>
                    </b-col>
<!--                    <b-col md="6">
                      <b-form-group label="Viz Type (display)" label-align="left">
                        <b-form-input v-model="selectedVizlet.viztype_name" placeholder="SimpleBar" />
                      </b-form-group>
                    </b-col>-->
                    <b-col md="4">
                      <b-form-group label="Sequence" label-align="left">
                        <b-form-input type="number" v-model.number="selectedVizlet.sequence" />
                      </b-form-group>
                    </b-col>
                    <b-col md="4">
                      <b-form-group label="Visible" label-align="left">
                        <b-form-checkbox v-model="selectedVizlet.visible">
                          Show on screen
                        </b-form-checkbox>
                      </b-form-group>
                    </b-col>
                    <b-col md="4">
                      <b-form-group label="Middle Title" label-align="left">
                        <b-form-checkbox v-model="selectedVizlet.middle_title_flag">
                          Center-align title
                        </b-form-checkbox>
                      </b-form-group>
                    </b-col>
<!--                    <b-col md="12">
                      <b-form-group label="Drilldown URL" label-align="left">
                        <b-form-input v-model="selectedVizlet.drilldown_url" placeholder="https://…" />
                      </b-form-group>
                    </b-col>-->
                  </b-row>
                </b-card-body>
              </b-collapse>
            </b-card>

            <!-- Accordion: Layout & Size -->
<!--            <b-card no-body class="ai-acc">
              <b-card-header class="p-0">
                <b-button block v-b-toggle.acc-layout variant="link" class="ai-acc-btn">
                  Layout &amp; Size
                </b-button>
              </b-card-header>
              <b-collapse id="acc-layout" :visible="hideVizletBasicSection" accordion="vizlet-acc">
                <b-card-body>
                  <b-row>
                    <b-col md="4">
                      <b-form-group label="Chart Width (px)" label-align="left">
                        <b-form-input type="number" v-model.number="selectedVizlet.layout.chart_width" />
                      </b-form-group>
                    </b-col>
                    <b-col md="4">
                      <b-form-group label="Chart Height (px)" label-align="left">
                        <b-form-input type="number" v-model.number="selectedVizlet.layout.chart_height" />
                      </b-form-group>
                    </b-col>
                    <b-col md="4">
                      <b-form-group label="Scrollbar" label-align="left">
                        <b-form-checkbox v-model="selectedVizlet.scrollbar">
                          Enable scrollbar
                        </b-form-checkbox>
                      </b-form-group>
                    </b-col>
                    <b-col md="3">
                      <b-form-group label="Padding Top" label-align="left">
                        <b-form-input type="number" v-model.number="selectedVizlet.layout.pm_top" />
                      </b-form-group>
                    </b-col>
                    <b-col md="3">
                      <b-form-group label="Padding Bottom" label-align="left">
                        <b-form-input type="number" v-model.number="selectedVizlet.layout.pm_bottom" />
                      </b-form-group>
                    </b-col>
                    <b-col md="3">
                      <b-form-group label="Padding Left" label-align="left">
                        <b-form-input type="number" v-model.number="selectedVizlet.layout.pm_left" />
                      </b-form-group>
                    </b-col>
                    <b-col md="3">
                      <b-form-group label="Padding Right" label-align="left">
                        <b-form-input type="number" v-model.number="selectedVizlet.layout.pm_right" />
                      </b-form-group>
                    </b-col>
                    <b-col md="12">
                      <b-form-group
                        label="Vizunit Width Array (JSON)"
                        label-align="left"
                        description="e.g. [12] for full width, [6,6] for side-by-side."
                      >
                        <b-form-input
                          :value="jsonPretty(selectedVizlet.layout.vizunit_width_array)"
                          @input="onJsonEdit($event, selectedVizlet.layout, 'vizunit_width_array', [])"
                          placeholder="[12]"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-card-body>
              </b-collapse>
            </b-card>-->

            <!-- Accordion: Axis & Labels -->
            <b-card no-body class="ai-acc">
              <b-card-header class="p-0">
                <b-button block v-b-toggle.acc-axis variant="link" class="ai-acc-btn">
                  Axis &amp; Labels
                </b-button>
              </b-card-header>
              <b-collapse id="acc-axis" accordion="vizlet-acc">
                <b-card-body>
                  <b-row>
                    <b-col md="6">
                      <b-form-group label="X1 Axis Display Name" label-align="left">
                        <b-form-input v-model="selectedVizlet.axis.x1_axis_display_name" />
                      </b-form-group>
                    </b-col>
                    <b-col md="6">
                      <b-form-group label="Y1 Axis Display Name" label-align="left">
                        <b-form-input v-model="selectedVizlet.axis.y1_axis_display_name" />
                      </b-form-group>
                    </b-col>
                    <b-col md="6">
                      <b-form-group label="X2 Axis Display Name" label-align="left">
                        <b-form-input v-model="selectedVizlet.axis.x2_axis_display_name" />
                      </b-form-group>
                    </b-col>
                    <b-col md="6">
                      <b-form-group label="Y2 Axis Display Name" label-align="left">
                        <b-form-input v-model="selectedVizlet.axis.y2_axis_display_name" />
                      </b-form-group>
                    </b-col>
<!--                    <b-col md="4">
                      <b-form-group label="Axis Font Size" label-align="left">
                        <b-form-input type="number" v-model.number="selectedVizlet.layout.axis_font_size" />
                      </b-form-group>
                    </b-col>
                    <b-col md="4">
                      <b-form-group label="Axis Font Color" label-align="left">
                        <b-form-input v-model="selectedVizlet.layout.axis_font_color" />
                      </b-form-group>
                    </b-col>
                    <b-col md="4">
                      <b-form-group label="Axis Label Distance" label-align="left">
                        <b-form-input type="number" v-model.number="selectedVizlet.layout.axis_label_distance" />
                      </b-form-group>
                    </b-col>
                    <b-col md="4">
                      <b-form-group label="Axis Label Font Size" label-align="left">
                        <b-form-input type="number" v-model.number="selectedVizlet.layout.axis_label_font_size" />
                      </b-form-group>
                    </b-col>
                    <b-col md="4">
                      <b-form-group label="Axis Label Font Color" label-align="left">
                        <b-form-input v-model="selectedVizlet.layout.axis_label_font_color" />
                      </b-form-group>
                    </b-col>
                    <b-col md="6">
                      <b-form-group label="X1 Metric Tooltip" label-align="left">
                        <b-form-input v-model="selectedVizlet.axis.x1_metric_tooltip" />
                      </b-form-group>
                    </b-col>
                    <b-col md="6">
                      <b-form-group label="Y1 Metric Tooltip" label-align="left">
                        <b-form-input v-model="selectedVizlet.axis.y1_metric_tooltip" />
                      </b-form-group>
                    </b-col>
                    <b-col md="6">
                      <b-form-group label="Y2 Metric Tooltip" label-align="left">
                        <b-form-input v-model="selectedVizlet.axis.y2_metric_tooltip" />
                      </b-form-group>
                    </b-col>
                    <b-col md="6">
                      <b-form-group label="Legend Tooltip" label-align="left">
                        <b-form-input v-model="selectedVizlet.axis.legend_metric_tooltip" />
                      </b-form-group>
                    </b-col>-->
                  </b-row>
                </b-card-body>
              </b-collapse>


            </b-card>

            <!-- Accordion: Metrics & Mapping -->
            <b-card no-body class="ai-acc">
              <b-card-header class="p-0">
                <b-button block v-b-toggle.acc-metrics variant="link" class="ai-acc-btn">
                  Metrics &amp; Mapping
                </b-button>
              </b-card-header>
              <b-collapse id="acc-metrics" accordion="vizlet-acc">
                <b-card-body>
                  <p class="text-muted small mb-3">
                    Map SQL columns from your vizlet DataSQL (see Chart Preview above) to chart roles.
                    Dimensions feed X-axis / categories; metrics feed Y-axis / values.
                  </p>
                  <b-row>
                    <b-col md="6">
                      <b-form-group label="X Axis (dim1)" label-align="left">
                        <b-form-select
                          v-model="selectedVizlet.axis.x1_data_field_name"
                          :options="vizletColumnOptions"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col md="6">
                      <b-form-group label="Y Axis (metric1)" label-align="left">
                        <b-form-select
                          v-model="selectedVizlet.axis.y1_data_field_name"
                          :options="vizletColumnOptions"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col md="6">
                      <b-form-group label="X2 Data Field" label-align="left">
                        <b-form-select
                          v-model="selectedVizlet.axis.x2_data_field_name"
                          :options="vizletColumnOptions"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col md="6">
                      <b-form-group label="Y2 Data Field" label-align="left">
                        <b-form-select
                          v-model="selectedVizlet.axis.y2_data_field_name"
                          :options="vizletColumnOptions"
                        />
                      </b-form-group>
                    </b-col>
<!--                    <b-col md="4">
                      <b-form-group label="Y1 Metric Unit" label-align="left">
                        <b-form-input v-model="selectedVizlet.axis.y1_metric_unit" />
                      </b-form-group>
                    </b-col>
                    <b-col md="4">
                      <b-form-group label="Y1 Metric Divisor" label-align="left">
                        <b-form-input type="number" v-model.number="selectedVizlet.axis.y1_metric_divisor" />
                      </b-form-group>
                    </b-col>
                    <b-col md="4">
                      <b-form-group label="High/Low Better" label-align="left">
                        <b-form-select
                          v-model="selectedVizlet.axis.high_low_better"
                          :options="highLowOptions"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col md="4">
                      <b-form-group label="Number System" label-align="left">
                        <b-form-select
                          v-model="selectedVizlet.axis.number_system"
                          :options="numberSystemOptions"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col md="4">
                      <b-form-group label="Number Display" label-align="left">
                        <b-form-select
                          v-model="selectedVizlet.axis.number_display_type"
                          :options="numberDisplayOptions"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col md="4">
                      <b-form-group label="X1 D3 Scale" label-align="left">
                        <b-form-select v-model="selectedVizlet.axis.x1_d3scale_type" :options="d3ScaleOptions" />
                      </b-form-group>
                    </b-col>
                    <b-col md="4">
                      <b-form-group label="Y1 D3 Scale" label-align="left">
                        <b-form-select v-model="selectedVizlet.axis.y1_d3scale_type" :options="d3ScaleOptions" />
                      </b-form-group>
                    </b-col>
                    <b-col md="4">
                      <b-form-group label="Y2 D3 Scale" label-align="left">
                        <b-form-select v-model="selectedVizlet.axis.y2_d3scale_type" :options="d3ScaleOptions" />
                      </b-form-group>
                    </b-col>-->
                  </b-row>
                </b-card-body>
              </b-collapse>
            </b-card>

            <!-- Accordion: Styling & Behavior -->
<!--
            <b-card no-body class="ai-acc">
              <b-card-header class="p-0">
                <b-button block v-b-toggle.acc-styling variant="link" class="ai-acc-btn">
                  Styling &amp; Behavior
                </b-button>
              </b-card-header>
              <b-collapse id="acc-styling" accordion="vizlet-acc">
                <b-card-body>
                  <b-row>
                    <b-col md="6">
                      <b-form-group label="Colour Scheme" label-align="left">
                        <b-form-input v-model="selectedVizlet.axis.colourscheme1" placeholder="scheme1" />
                      </b-form-group>
                    </b-col>
                    <b-col md="6">
                      <b-form-group label="Card Style" label-align="left">
                        <b-form-input v-model="selectedVizlet.card_style" placeholder="min-width:30rem;" />
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-card-body>
              </b-collapse>
            </b-card>

            &lt;!&ndash; Accordion: Advanced Config &ndash;&gt;
            <b-card no-body class="ai-acc">
              <b-card-header class="p-0">
                <b-button block v-b-toggle.acc-advanced variant="link" class="ai-acc-btn">
                  Advanced Config
                </b-button>
              </b-card-header>
              <b-collapse id="acc-advanced" accordion="vizlet-acc">
                <b-card-body>
                  <b-row>
                    <b-col
                      v-for="n in 7"
                      :key="'vs-' + n"
                      md="6"
                    >
                      <b-form-group :label="'viz_specific' + n" label-align="left">
                        <b-form-input
                          :value="selectedVizlet.axis['viz_specific' + n]"
                          @input="(v) => (selectedVizlet.axis['viz_specific' + n] = v || null)"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col md="12">
                      <b-form-group label="JSON File" label-align="left">
                        <b-form-input v-model="selectedVizlet.axis.json_file" />
                      </b-form-group>
                    </b-col>
                    <b-col md="12">
                      <b-form-group label="Tab Report JSON" label-align="left">
                        <b-form-textarea
                          :value="jsonPretty(selectedVizlet.axis.tab_report_json)"
                          @input="onJsonEdit($event, selectedVizlet.axis, 'tab_report_json', {})"
                          rows="3"
                          class="sql-textarea"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col md="12">
                      <b-form-group label="Report Header Column DP (JSON)" label-align="left">
                        <b-form-textarea
                          :value="jsonPretty(selectedVizlet.axis.report_header_column_dp)"
                          @input="onJsonEdit($event, selectedVizlet.axis, 'report_header_column_dp', {})"
                          rows="3"
                          class="sql-textarea"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col md="12">
                      <b-form-group label="X1 Axis Label Char Limits (JSON)" label-align="left">
                        <b-form-textarea
                          :value="jsonPretty(selectedVizlet.axis.x1_axis_label_char_limits_json)"
                          @input="
                            onJsonEdit(
                              $event,
                              selectedVizlet.axis,
                              'x1_axis_label_char_limits_json',
                              {},
                            )
                          "
                          rows="3"
                          class="sql-textarea"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col md="12">
                      <b-form-group label="Insight Navigator (JSON)" label-align="left">
                        <b-form-textarea
                          :value="jsonPretty(selectedVizlet.axis.insight_navigator)"
                          @input="onJsonEdit($event, selectedVizlet.axis, 'insight_navigator', {})"
                          rows="3"
                          class="sql-textarea"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-card-body>
              </b-collapse>
            </b-card>
-->


          </div>
        </section>
      </div>

      <!-- Footer: actions -->
      <div class="d-flex justify-content-end mt-4 ai-footer-actions">
        <b-button variant="danger" @click="resetForm" class="mr-2">Reset</b-button>
        <b-button variant="primary" @click="submitForm">Submit</b-button>
      </div>
    </div>
  </ValidationObserver>
</template>

<script>
import axios from "axios";
import {  ValidationObserver, extend } from "vee-validate";
import { required } from "vee-validate/dist/rules";
import { apiAbort } from "@/common/apiAbort";
import {
  generateMenuConfigFromSql,
  generateMenuUiFromSqls,
  getSampleMenuUiConfig,
  GENERATE_UI_SYSTEM_PROMPT,
} from "@/services/aiMenuService";
import LlmChat from "@/components/LlmChat.vue";
import { FwVizChartInfo } from "@/models";
import { Parser as SqlParser } from "node-sql-parser";
import {
  standardizeSelectAliases,
  validateSelectListForAliasRewrite,
  exprContainsAggregate,
  classifyPreviewOrFetchError,
} from "@/common/sqlDataSqlAlias";

const sqlParser = new SqlParser();

extend("required", {
  ...required,
  message: "{_field_} is required",
});

/** Factories: keep new Screen / Screenlet / Vizlet shapes in sync with the LLM contract. */
function makeListbox() {
  return {
    listbox_sql: "",
    filter_model: "anywhichway/freeform",
    filter_display: [],
  };
}
function makeDateRangeConfig() {
  return { default_range: "last_30_days", allow_custom: true };
}
function makeScreen(overrides = {}) {
  return {
    screen_title: "",
    screen_type: "ST01",
    screen_listbox: makeListbox(),
    date_range_filter_display: true,
    date_range_filter_config: makeDateRangeConfig(),
    screen_card_style: null,
    orderby_text_json: {},
    screenlets: [],
    ...overrides,
  };
}
function makeScreenlet(overrides = {}) {
  return {
    screenlet_type: "SL01",
    sequence: 1,
    title: "",
    title_short: "",
    title_info: "",
    default_count: 1,
    max_count: 1,
    addition_flag: false,
    screenlet_card_style: null,
    screenlet_listbox: makeListbox(),
    date_range_filter_display: true,
    date_range_filter_config: makeDateRangeConfig(),
    orderby_text_json: {},
    vizlets: [],
    ...overrides,
  };
}
function makeVizletLayout() {
  return {
    chart_width: 900,
    chart_height: 400,
    pm_top: 40,
    pm_bottom: 100,
    pm_left: 80,
    pm_right: 40,
    vizunit_width_array: [12],
    axis_font_size: 12,
    axis_font_color: "#333333",
    axis_label_font_size: 14,
    axis_label_font_color: "#333333",
    axis_label_distance: 18,
  };
}
function makeVizletAxis() {
  return {
    x1_data_field_name: "dim1_name",
    x2_data_field_name: null,
    y1_data_field_name: "metric1",
    y2_data_field_name: null,
    x1_axis_display_name: "",
    x2_axis_display_name: null,
    y1_axis_display_name: "",
    y2_axis_display_name: null,
    x1_metric_tooltip: null,
    y1_metric_tooltip: null,
    y2_metric_tooltip: null,
    legend_metric_tooltip: null,
    y1_metric_unit: null,
    y2_metric_unit: null,
    y1_metric_divisor: null,
    y2_metric_divisor: null,
    colourscheme1: "scheme1",
    number_system: "Western",
    number_display_type: "absolute",
    high_low_better: null,
    x1_d3scale_type: null,
    y1_d3scale_type: null,
    y2_d3scale_type: null,
    x1_axis_label_char_limits_json: {},
    json_file: null,
    report_header_column_dp: {},
    report_header_column_dp_wordwrap: {},
    all_header_column_dp: [],
    tab_report_json: {},
    insight_navigator: {},
    viz_specific1: null,
    viz_specific2: null,
    viz_specific3: null,
    viz_specific4: null,
    viz_specific5: null,
    viz_specific6: null,
    viz_specific7: null,
  };
}
function makeVizlet(overrides = {}) {
  return {
    sequence: 1,
    viztype_function_name: "simpleBarChart",
    viztype_name: "SimpleBar",
    vizlet_title: "",
    vizlet_title_short: "",
    vizlet_title_info: "",
    visible: true,
    card_style: "min-width:30rem;",
    scrollbar: true,
    middle_title_flag: true,
    drilldown_url: null,
    datasql: "",
    download_sql: null,
    layout: makeVizletLayout(),
    axis: makeVizletAxis(),
    ...overrides,
  };
}

/** Strip tenant keys (client_id, function_id, menu_id) at any depth — they live in tenant, never UI. */
function stripTenant(obj) {
  if (Array.isArray(obj)) return obj.map(stripTenant);
  if (obj && typeof obj === "object") {
    const out = {};
    Object.keys(obj).forEach((k) => {
      if (k === "tenant" || k === "client_id" || k === "function_id") return;
      out[k] = stripTenant(obj[k]);
    });
    return out;
  }
  return obj;
}

/**
 * True when the user's source query carries trailing clauses that the LLM-truncated
 * vizlet SQL dropped (non-empty LLM sql otherwise wins over empty — see applyUiPayload).
 */
function sourceSqlShouldReplaceVizletDatasql(source, candidate) {
  const s = String(source || "").trim();
  const c = String(candidate || "").trim();
  if (!s) return false;
  if (!c) return true;
  const trailingClauseRes = [
    /\bgroup\s+by\b/i,
    /\bhaving\b/i,
    /\border\s+by\b/i,
  ];
  return trailingClauseRes.some((re) => re.test(s) && !re.test(c));
}

export default {
  name: "AiMenuConfig",
  props: {
    embedded: { type: Boolean, default: false },
    selectedOptions: { type: Object, default: () => ({}) },
    filters: { type: Object, default: () => ({}) },
  },
  components: {
    // ValidationProvider,
    ValidationObserver,
    LlmChat,
  },
  watch: {
    filters: {
      handler() {
        this.initMenuDefaults(this.activeMenuId);
        this.$nextTick(() => this.renderExistingChart());
      },
      deep: true,
    },
    selectedPreviewVizType(newVal, oldVal) {
      if (newVal === oldVal) return;
      this.$nextTick(() => {
        this.onPreviewVizTypeChange().catch(() => {});
      });
    },
    /**
     * Keep the active vizlet's datasql in sync with the preview DataSQL textbox so the
     * Step-3 preview card always reflects the currently-selected vizlet.
     */
    dataSqlId(newVal) {
      if (this.selectedNode.type === "vizlet" && this.selectedVizlet) {
        this.selectedVizlet.datasql = newVal || "";
      }
    },
  },
  created() {
    this.initMenuDefaults(this.activeMenuId);
  },
  data: () => ({
    existingChartElId: `viz-ex-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    previewChartElId: `viz-pv-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,

    // ---- 3-step progressive layout ----
    currentStep: 1,
    steps: [
      { id: 1, label: "Screen" },
      { id: 2, label: "Screenlet" },
      { id: 3, label: "Vizlet" },
    ],

    // ---- Multi-SQL Generate UI input ----
    datasqls: [""],
    /** Natural-language intent for generate_forewarn_viz_config (user_prompt). */
    generateUiUserPrompt: "",
    generateUiLoading: false,
    generateUiError: "",

    // ---- Nested aiConfig (matches LLM JSON, tenant-stripped) ----
    aiConfig: {
      config_version: "2.0",
      screens: [],
    },
    /** Selection path into aiConfig. type ∈ {null, 'screen', 'screenlet', 'vizlet'}. */
    selectedNode: { type: null, si: 0, sli: 0, vi: 0 },
    /** Tree expand/collapse state keyed by 'sc-<i>' / 'sl-<i>-<j>'. */
    expanded: {},

    // ---- Preview panel (Step 3) ----
    selectedPreviewVizType: "tableBarChart",
    previewVizTypeOptions: [
      { value: "tableBarChart", text: "Table + bar (mapped columns)" },
      { value: "simpleBarChart", text: "Vertical bar" },
      { value: "horizontalbarChart", text: "Horizontal bar" },
      { value: "d3PieChart", text: "Pie chart" },
      { value: "multiLineChart", text: "Multi-line chart" },
    ],

    // ---- Menu master defaults (parent name, screen name, etc.) ----
    loadMenu: true,
    defaultParentMenuId: null,
    parentMenuDisplay: "my_dashboard",
    parentMenuIdOptions: [],

    menuId: "",
    defaultMenuName: "",
    menuName: "",
    screenName: "",

    // Legacy fields that older submit/preview code still reads/writes.
    filterSqlId: "",
    defaultFilterSyspk: "",
    filterSyspk: "",
    dataSqlId: "",
    vizTypeName: "",
    vizTypeOptions: [],
    vizSizeId: "VS72",
    vizSizeOptions: [],
    x_axisName: "",
    y_axisName: "",
    vizTitleName: "",
    x_axisTooltip: "",
    y_axisTooltip: "",
    x_axisDataName: "",
    y_axisDataName: "",
    tableHeaders: [],
    nextFieldId: 0,

    // ---- UI state ----
    aiChatSidebarOpen: false,
    aiGenerateLoading: false,
    aiGenerateError: "",
    lastAiMenuConfigPayload: null,
    lastAiMenuConfigSql: "",
    existingVizFunction: "",
    previewLoading: false,
    previewError: "",
    previewData: [],
    previewConfig: {},
    selectedColumns: {},
    selectedTable: "",
    vizFunction: "",
    previewTableBarRows: [],
    tableBarTypeOptions: [
      { value: "Dimension", text: "Dimension" },
      { value: "Metric", text: "Metric" },
    ],
    tableBarGraphOptions: [
      { value: "no", text: "No" },
      { value: "yes", text: "Yes" },
    ],

    // ---- Dropdown options for vizlet mapping/axis fields ----
    d3ScaleOptions: [
      { value: null, text: "(auto)" },
      { value: "bandScale", text: "bandScale" },
      { value: "linearScale", text: "linearScale" },
      { value: "timeScale", text: "timeScale" },
      { value: "ordinalScale", text: "ordinalScale" },
    ],
    numberSystemOptions: [
      { value: "Western", text: "Western (K, M, B)" },
      { value: "Indian", text: "Indian (Lakh, Crore)" },
    ],
    numberDisplayOptions: [
      { value: "absolute", text: "Absolute" },
      { value: "percent", text: "Percent" },
      { value: "compact", text: "Compact" },
    ],
    highLowOptions: [
      { value: null, text: "(neutral)" },
      { value: "high", text: "High is better" },
      { value: "low", text: "Low is better" },
    ],

    defaultChartData: {
      datasql: "",
      width: 800,
      height: 300,
      axisFontSize: 8,
      axisFontColor: "black",
      axisLabelFontSize: 6,
      axisLabelFontColor: "black",
      axisLabelDistance: 8,
      x1AxisDisplayName: "",
      x2AxisDisplayName: "",
      y1AxisDisplayName: "",
      y2AxisDisplayName: "",
      x1DataFieldName: "",
      x2DataFieldName: "",
      y1DataFieldName: "",
      y2DataFieldName: "",
      reportHeaderColumnDp: "",
      x1D3ScaleType: "timeScale",
      x2D3ScaleType: "timeScale",
      y1D3ScaleType: "linearScale",
      y2D3ScaleType: "linearScale",
      reportHeaderColumnDpWordwrap: {
        wordwrap: { "table heading": "0", "table data": "0" },
      },
      tabReportJson: {},
      x1AxisLabelCharLimitsJson: {
        break: { value1: "0", value2: "/( |,|_|(?=[A-Z]))/" },
      },
      margin: { top: 10, right: 50, bottom: 50, left: 50 },
    },
  }),
  computed: {
    activeMenuId() {
      const hasFilters = this.filters && Object.keys(this.filters).length > 0;
      if (hasFilters) {
        const sel = this.selectedOptions || {};
        const ids = sel.menu_id;
        if (Array.isArray(ids) && ids.length && ids[0]) return ids[0];
      }
      return "I11YTD";
    },
    hasSelection() {
      return !!this.selectedNode && !!this.selectedNode.type;
    },
    hasAnyConfig() {
      return !!(this.aiConfig.screens && this.aiConfig.screens.length);
    },
    canGenerateUi() {
      return (
        !this.generateUiLoading &&
        (this.datasqls || []).some((q) => (q || "").trim().length > 0)
      );
    },
    /** Sent as `llmUserId` → Kotlin `user_id` on the LLM proxy (matches html-llm-chat). */
    llmProxyUserId() {
      const u = (this.$store.state.userName || "").trim();
      if (u) return u;
      const id = this.$store.state.userId;
      return id != null && id !== 0 ? String(id) : "";
    },
    /** Hint for LLM: derived from Step 3 Preview viz dropdown only (no separate chart picker). */
    effectiveGenerateUiChartType() {
      return this.previewVizTypeToRequestedChartType(this.selectedPreviewVizType);
    },
    selectedScreen() {
      const { si } = this.selectedNode;
      return this.aiConfig.screens[si] || null;
    },
    selectedScreenlet() {
      const s = this.selectedScreen;
      if (!s) return null;
      return s.screenlets[this.selectedNode.sli] || null;
    },
    selectedVizlet() {
      const sl = this.selectedScreenlet;
      if (!sl) return null;
      return sl.vizlets[this.selectedNode.vi] || null;
    },
    /** Single-screenlet + single-vizlet: flatten tree and combine screen/screenlet forms. */
    showScreenLevelForm() {
      if (this.selectedNode.type === "screen" && this.selectedScreen) return true;
      if (
        this.selectedNode.type === "screenlet" &&
        this.selectedScreenlet &&
        this.mergeScreenScreenlet(this.selectedNode.si)
      ) {
        return true;
      }
      return false;
    },
    /** Screenlet row bound when Screen + Screenlet are merged in one form. */
    mergedScreenletForForm() {
      const s = this.selectedScreen;
      const si = this.selectedNode.si;
      if (!s || !this.mergeScreenScreenlet(si)) return null;
      return s.screenlets[0] || null;
    },
    /** Hide vizlet "Basic Info" accordion when the screenlet has only one vizlet. */
    hideVizletBasicSection() {
      const sl = this.selectedScreenlet;
      return !!(sl && Array.isArray(sl.vizlets) && sl.vizlets.length === 1);
    },
    /** Columns parsed from the current vizlet's DataSQL → drive mapping dropdowns. */
    vizletColumnOptions() {
      const sql = (this.dataSqlId || "").trim();
      const base = [{ value: null, text: "—" }];
      if (!sql) return base;
      try {
        let ast = sqlParser.astify(sql, { database: "PostgreSQL" });
        if (Array.isArray(ast)) ast = ast[0];
        if (!ast || ast.type !== "select" || !Array.isArray(ast.columns)) {
          return base;
        }
        const opts = ast.columns.map((col, idx) => {
          const alias =
            col.as || (col.expr && col.expr.column) || `col${idx + 1}`;
          return { value: alias, text: alias };
        });
        return base.concat(opts);
      } catch (e) {
        return base;
      }
    },
    vizParams() {
      const vizData = this.$store.state.fwConfigScreenElement.filter(
        (item) => item.menuId === this.activeMenuId,
      );
      if (!vizData.length) return {};
      const allVizType = this.$store.state.vizType;
      const matched = allVizType.find(
        (t) => t.viztypeId === vizData[0].vizletId,
      );
      return matched?.vizParams || {};
    },
    previewVizParamsFromMaster() {
      const fn = String(this.selectedPreviewVizType || "").trim();
      if (!fn) return {};
      const all = this.$store.state.vizType || [];
      const t = all.find(
        (x) => String(x.viztypeFunctionName || "").trim() === fn,
      );
      const raw = t && t.vizParams;
      if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
      const out = {};
      Object.keys(raw).forEach((k) => {
        if (!k) return;
        const v = raw[k];
        if (v == null) return;
        const s = typeof v === "string" ? v.trim() : String(v).trim();
        if (s) out[String(k)] = s;
      });
      return out;
    },
    fetchDefaultvalues() {
      return this.$store.state.fwConfigScreenElement.filter(
        (item) => item.menuId === this.activeMenuId,
      );
    },
    firstScreenElement() {
      const rows = this.fetchDefaultvalues;
      return rows && rows.length ? rows[0] : null;
    },
    showAxisConfigFields() {
      return (
        !!this.selectedPreviewVizType &&
        this.selectedPreviewVizType !== "tableBarChart"
      );
    },
    cleanedDataSql() {
      let rawSql = this.fetchDefaultvalues?.[0]?.datasql || "";
      if (!rawSql) return "";
      rawSql = rawSql
        .replace(/\bfw_where_clause\b/gi, "")
        .replace(/\bfw_and_clause\b/gi, "")
        .replace(/\bfw_core_config_conn\b/gi, "");
      const selectMatch = rawSql.match(/select([\s\S]*?)from/i);
      if (!selectMatch) return rawSql.trim();
      const selectPart = selectMatch[1];
      const afterFrom = rawSql.substring(
        selectMatch.index + selectMatch[0].length - 4,
      );
      const columns = selectPart
        .replace(/\n/g, " ")
        .match(/(?:[^,(]|\([^)]*\))+/g);
      const aliasMap = {};
      const newColumns = [];
      columns.forEach((col) => {
        let column = col.trim();
        const aliasMatch = column.match(/\bas\s+([a-zA-Z0-9_]+)/i);
        if (aliasMatch) {
          const alias = aliasMatch[1];
          const original = column.replace(/\bas\s+[a-zA-Z0-9_]+/i, "").trim();
          aliasMap[alias] = original;
          newColumns.push(original);
        } else {
          newColumns.push(column);
        }
      });
      let updatedAfterFrom = afterFrom;
      Object.keys(aliasMap).forEach((alias) => {
        const regex = new RegExp(`\\b${alias}\\b`, "gi");
        updatedAfterFrom = updatedAfterFrom.replace(regex, aliasMap[alias]);
      });
      const finalSql = `select ${newColumns.join(", ")} ${updatedAfterFrom}`;
      return finalSql.replace(/\n\s*\n/g, "\n").trim();
    },
    parsedExistingSqlAliases() {
      const rawSql = this.fetchDefaultvalues?.[0]?.datasql || "";
      if (!rawSql) return {};
      const aliasMap = {};
      try {
        let ast = sqlParser.astify(rawSql, { database: "PostgreSQL" });
        if (Array.isArray(ast)) ast = ast[0];
        if (!ast || ast.type !== "select" || !Array.isArray(ast.columns))
          return {};
        ast.columns.forEach((col) => {
          if (!col.as) return;
          const simpleSelectAst = {
            with: null,
            type: "select",
            options: null,
            distinct: null,
            columns: [{ expr: col.expr, as: null }],
            into: { position: null },
            from: null,
            where: null,
            groupby: null,
            having: null,
            orderby: null,
            limit: null,
            locking_read: null,
            window: null,
            collate: null,
          };
          let exprSql = "";
          try {
            const sqlCol = sqlParser.sqlify(simpleSelectAst, {
              database: "PostgreSQL",
            });
            exprSql = sqlCol.replace(/^select\s+/i, "").trim();
          } catch (e) {
            exprSql = "";
          }
          if (exprSql) aliasMap[col.as] = exprSql;
        });
      } catch (e) {
        return {};
      }
      return aliasMap;
    },
    isPreviewEnabled() {
      const q = (this.dataSqlId || "").trim();
      if (!q) return false;
      if (!this.checkSqlClientRules(q).ok) return false;
      if (this.selectedPreviewVizType === "tableBarChart") return true;
      return this.isStandardPreviewSqlReady(q);
    },
    existingMappingRows() {
      if (!this.vizParams || !Object.keys(this.vizParams).length) return [];
      const aliasMap = this.parsedExistingSqlAliases;
      return Object.entries(this.vizParams).map(([label, alias]) => ({
        axisLabel: label,
        alias,
        expression: aliasMap[alias] || "—",
        type:
          typeof alias === "string" && alias.toLowerCase().startsWith("metric")
            ? "Metric"
            : "Dimension",
      }));
    },
    tableBarHasGraph() {
      if (this.existingVizFunction !== "tableBarChart") return false;
      const headerMap =
        this.fetchDefaultvalues?.[0]?.reportHeaderColumnDp || {};
      return Object.values(headerMap).some(
        (v) => typeof v === "string" && v.toLowerCase().startsWith("graph"),
      );
    },
    tableBarExistingMappingRows() {
      if (this.existingVizFunction !== "tableBarChart") return [];
      const headerMap =
        this.fetchDefaultvalues?.[0]?.reportHeaderColumnDp || {};
      const entries = Object.entries(headerMap).filter(
        ([, value]) =>
          !(
            typeof value === "string" && value.toLowerCase().startsWith("graph")
          ),
      );
      const graphLabelSet = new Set();
      Object.entries(headerMap).forEach(([label, value]) => {
        if (
          typeof value === "string" &&
          value.toLowerCase().startsWith("graph")
        ) {
          const baseLabel = label.replace(/#+.*$/, "").replace(/#$/, "");
          graphLabelSet.add(baseLabel);
        }
      });
      let index = 1;
      return entries.map(([label, value]) => {
        const isMetric =
          typeof value === "string" && value.toLowerCase().startsWith("metric");
        const hasGraph = isMetric && graphLabelSet.has(label);
        const required = `Col${index++}`;
        return {
          required,
          column: label,
          field: value,
          type: isMetric ? "Metric" : "Dimension",
          graph: hasGraph ? "yes" : "no",
        };
      });
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.renderExistingChart();
    });
  },
  methods: {
    // =======================================================================
    // Progressive layout helpers (tree / stepper / selection)
    // =======================================================================
    gotoStep(step) {
      if (!this.isStepReachable(step)) return;
      this.currentStep = step;
      // Snap selection to the first node of that level if nothing matching is selected.
      if (step === 1) {
        if (this.aiConfig.screens.length) {
          this.selectNode({ type: "screen", si: 0 });
        }
      } else if (step === 2) {
        const s = this.aiConfig.screens[this.selectedNode.si] || this.aiConfig.screens[0];
        if (s && s.screenlets.length) {
          this.selectNode({
            type: "screenlet",
            si: s === this.aiConfig.screens[this.selectedNode.si] ? this.selectedNode.si : 0,
            sli: 0,
          });
        }
      } else if (step === 3) {
        const si = this.selectedNode.si;
        const sli = this.selectedNode.sli;
        const s = this.aiConfig.screens[si];
        const sl = s && s.screenlets[sli];
        if (sl && sl.vizlets.length) {
          this.selectNode({ type: "vizlet", si, sli, vi: 0 });
        }
      }
    },
    isStepReachable(step) {
      if (step === 1) return this.aiConfig.screens.length > 0;
      if (step === 2) {
        const s = this.aiConfig.screens[this.selectedNode.si] || this.aiConfig.screens[0];
        return !!(s && s.screenlets.length);
      }
      if (step === 3) {
        const s = this.aiConfig.screens[this.selectedNode.si];
        const sl = s && s.screenlets[this.selectedNode.sli];
        return !!(sl && sl.vizlets.length);
      }
      return false;
    },
    isSelected(type, si, sli, vi) {
      if (type === "screen") {
        if (this.selectedNode.si !== si) return false;
        if (this.selectedNode.type === "screen") return true;
        if (
          this.mergeScreenScreenlet(si) &&
          this.selectedNode.type === "screenlet" &&
          this.selectedNode.sli === 0
        ) {
          return true;
        }
        return false;
      }
      if (this.selectedNode.type !== type) return false;
      if (type === "screenlet")
        return this.selectedNode.si === si && this.selectedNode.sli === sli;
      if (type === "vizlet")
        return (
          this.selectedNode.si === si &&
          this.selectedNode.sli === sli &&
          this.selectedNode.vi === vi
        );
      return false;
    },
    selectNode({ type, si = 0, sli = 0, vi = 0 }) {
      this.selectedNode = { type, si, sli, vi };
      // Keep the current step in sync with the selected node type.
      if (type === "screen") this.currentStep = 1;
      else if (type === "screenlet") this.currentStep = 2;
      else if (type === "vizlet") {
        this.currentStep = 3;
        this.$nextTick(() => this.hydratePreviewFromVizlet());
      }
      // Auto-expand parents so the selected node is visible in the tree.
      if (type === "screen") {
        this.$set(this.expanded, "sc-" + si, true);
      } else if (type === "screenlet") {
        this.$set(this.expanded, "sc-" + si, true);
        this.$set(this.expanded, "sl-" + si + "-" + sli, true);
      } else if (type === "vizlet") {
        this.$set(this.expanded, "sc-" + si, true);
        this.$set(this.expanded, "sl-" + si + "-" + sli, true);
      }
    },
    toggleExpand(key) {
      this.$set(this.expanded, key, !this.expanded[key]);
    },
    /** True when this screen has a single screenlet with a single vizlet — flatten tree and merge forms. */
    mergeScreenScreenlet(si) {
      const screen = this.aiConfig.screens[si];
      if (!screen || screen.screenlets.length !== 1) return false;
      return screen.screenlets[0].vizlets.length === 1;
    },
    addScreen() {
      const idx = this.aiConfig.screens.length;
      const screen = makeScreen({
        screen_title: `Screen ${idx + 1}`,
      });
      this.aiConfig.screens.push(screen);
      this.selectNode({ type: "screen", si: idx });
    },
    removeScreen(si) {
      this.aiConfig.screens.splice(si, 1);
      if (!this.aiConfig.screens.length) {
        this.selectedNode = { type: null, si: 0, sli: 0, vi: 0 };
      } else {
        this.selectNode({ type: "screen", si: Math.max(0, si - 1) });
      }
    },
    addScreenlet(si) {
      const screen = this.aiConfig.screens[si];
      if (!screen) return;
      const sli = screen.screenlets.length;
      screen.screenlets.push(
        makeScreenlet({
          sequence: sli + 1,
          title: `Screenlet ${sli + 1}`,
        }),
      );
      this.$set(this.expanded, "sc-" + si, true);
      this.selectNode({ type: "screenlet", si, sli });
    },
    removeScreenlet(si, sli) {
      const screen = this.aiConfig.screens[si];
      if (!screen) return;
      screen.screenlets.splice(sli, 1);
      if (!screen.screenlets.length) {
        this.selectNode({ type: "screen", si });
      } else {
        this.selectNode({
          type: "screenlet",
          si,
          sli: Math.max(0, sli - 1),
        });
      }
    },
    addVizlet(si, sli) {
      const sl = this.aiConfig.screens[si]?.screenlets[sli];
      if (!sl) return;
      const vi = sl.vizlets.length;
      sl.vizlets.push(
        makeVizlet({
          sequence: vi + 1,
          vizlet_title: `Vizlet ${vi + 1}`,
        }),
      );
      this.$set(this.expanded, "sc-" + si, true);
      this.$set(this.expanded, "sl-" + si + "-" + sli, true);
      this.selectNode({ type: "vizlet", si, sli, vi });
    },
    removeVizlet(si, sli, vi) {
      const sl = this.aiConfig.screens[si]?.screenlets[sli];
      if (!sl) return;
      sl.vizlets.splice(vi, 1);
      if (!sl.vizlets.length) {
        this.selectNode({ type: "screenlet", si, sli });
      } else {
        this.selectNode({
          type: "vizlet",
          si,
          sli,
          vi: Math.max(0, vi - 1),
        });
      }
    },

    // =======================================================================
    // Step 3 preview bridging: push/pull data between the preview card and the
    // currently-selected vizlet so the two views stay in sync.
    // =======================================================================
    hydratePreviewFromVizlet() {
      const v = this.selectedVizlet;
      if (!v) return;
      this.dataSqlId = v.datasql || "";
      this.selectedPreviewVizType =
        v.viztype_function_name || this.selectedPreviewVizType;
      // Mirror axis display text into the preview defaults for rendering.
      this.x_axisName = v.axis?.x1_axis_display_name || "";
      this.y_axisName = v.axis?.y1_axis_display_name || "";
      this.x_axisTooltip = v.axis?.x1_metric_tooltip || "";
      this.y_axisTooltip = v.axis?.y1_metric_tooltip || "";
      this.vizTitleName = v.vizlet_title || "";
      this.syncDefaultChartAxisLabelsFromForm();
    },
    onVizletFunctionChange() {
      // If the dropdown inside the vizlet accordion changes the chart function,
      // mirror to the preview selector (no extra LLM call).
      if (this.selectedVizlet && this.selectedVizlet.viztype_function_name) {
        this.selectedPreviewVizType = this.selectedVizlet.viztype_function_name;
      }
    },

    // =======================================================================
    // JSON editor helpers — let the user freely edit fragmentary JSON while
    // avoiding reactivity failures on malformed input.
    // =======================================================================
    jsonPretty(value) {
      if (value == null) return "";
      try {
        return JSON.stringify(value, null, 2);
      } catch (e) {
        return String(value);
      }
    },
    onJsonEdit(rawInput, target, key, fallback) {
      const text = (rawInput == null ? "" : String(rawInput)).trim();
      if (!text) {
        target[key] = fallback;
        return;
      }
      try {
        target[key] = JSON.parse(text);
      } catch (e) {
        // Keep the last good value; user will see the raw text until it parses.
      }
    },

    // =======================================================================
    // Generate UI flow: one LLM call produces the whole Screen → Screenlet →
    // Vizlet[] tree from an array of DataSQLs.
    // =======================================================================
    addDataSql() {
      this.datasqls.push("");
    },
    removeDataSql(i) {
      this.datasqls.splice(i, 1);
      if (!this.datasqls.length) this.datasqls.push("");
    },
    async generateUi() {
      this.generateUiError = "";
      const sqls = (this.datasqls || [])
        .map((s) => (s || "").trim())
        .filter(Boolean);
      if (!sqls.length) return;
      for (const q of sqls) {
        const chk = this.checkSqlClientRules(q);
        if (!chk.ok) {
          this.generateUiError = chk.message;
          return;
        }
      }

      this.generateUiLoading = true;
      try {
        let payload = null;
        let usedDemoFallback = false;
        try {
          const resp = await generateMenuUiFromSqls(sqls, {
            userPrompt: (this.generateUiUserPrompt || "").trim(),
            requestedChartType: this.effectiveGenerateUiChartType,
            systemPrompt: GENERATE_UI_SYSTEM_PROMPT,
            llmUserId: this.llmProxyUserId || undefined,
          });
          payload = resp && resp.data;
        } catch (primaryErr) {
          // Fallback 1: call the single-SQL endpoint once per SQL and synthesize
          // a nested payload so users can keep working until the backend ships
          // /api/ai/generate-menu-ui.
          payload = await this.synthesizeUiPayloadFromSingleSql(sqls);
          if (!payload) {
            // Fallback 2 (DEV-ONLY): load the bundled sample JSON so the UI
            // remains demo-able when both endpoints are unreachable. Remove
            // once the backend is live end-to-end.
            payload = getSampleMenuUiConfig();
            usedDemoFallback = true;
          }
        }

        this.applyUiPayload(payload, sqls);
        if (usedDemoFallback) {
          this.$bvToast.toast(
            "LLM endpoint unreachable. Loaded the bundled sample menu-UI JSON (dev fallback).",
            {
              title: "Demo data",
              variant: "warning",
              solid: true,
              autoHideDelay: 6000,
            },
          );
        } else {
          this.$bvToast.toast("UI generated from SQLs. Review the tree on the left.", {
            title: "AI",
            variant: "success",
            solid: true,
            autoHideDelay: 4000,
          });
        }
      } catch (e) {
        const d = e.response && e.response.data;
        this.generateUiError =
          (d && d.error) ||
          (typeof d === "string" ? d : null) ||
          e.message ||
          "Generate UI failed";
      } finally {
        this.generateUiLoading = false;
      }
    },
    /** TEMPORARY dev helper: load the bundled sample JSON directly, bypassing the LLM. */
    loadSampleUi() {
      const payload = getSampleMenuUiConfig();
      const sqls = [];
      const screenlets = (payload.screen && payload.screen.screenlets) || [];
      screenlets.forEach((sl) => {
        (sl.vizlets || []).forEach((vz) => {
          if (vz.datasql) sqls.push(vz.datasql);
        });
      });
      if (sqls.length) this.datasqls = sqls;
      this.applyUiPayload(payload, sqls);
      this.$bvToast.toast("Loaded bundled sample menu-UI JSON (dev fixture).", {
        title: "Demo data",
        variant: "info",
        solid: true,
        autoHideDelay: 4000,
      });
    },
    /** Per-SQL fallback: call the flat /api/ai/generate-menu-config endpoint. */
    async synthesizeUiPayloadFromSingleSql(sqls) {
      try {
        const responses = await Promise.all(
          sqls.map((sql) =>
            generateMenuConfigFromSql(sql, {
              vizTypeFunction: this.selectedPreviewVizType || "",
              vizParams: this.previewVizParamsFromMaster,
            }).then((r) => r.data || {}),
          ),
        );
        const first = responses[0] || {};
        const screen = makeScreen({
          screen_title: first.screen_title || this.defaultMenuName || "Screen",
        });
        const screenlet = makeScreenlet({
          title: first.menu_name || first.screen_title || "Screenlet",
          title_short: first.menu_name || "",
          title_info: first.analysis_summary || "",
        });
        screenlet.vizlets = responses.map((r, i) => {
          const sql = sqls[i];
          return makeVizlet({
            sequence: i + 1,
            viztype_function_name:
              (r.viz_suggestions && r.viz_suggestions[0]) ||
              this.selectedPreviewVizType ||
              "simpleBarChart",
            vizlet_title: r.screen_title || `Vizlet ${i + 1}`,
            vizlet_title_info: r.analysis_summary || "",
            datasql: sql,
            axis: Object.assign(makeVizletAxis(), {
              x1_axis_display_name: r.x_axis_name || "",
              y1_axis_display_name: r.y_axis_name || "",
              x1_metric_tooltip: r.x_axis_tooltip || null,
              y1_metric_tooltip: r.y_axis_tooltip || null,
            }),
          });
        });
        screen.screenlets = [screenlet];
        return { config_version: "2.0", screen };
      } catch (e) {
        return null;
      }
    },
    /** Map Preview viz function name → LLM requested_chart_type label. */
    previewVizTypeToRequestedChartType(fn) {
      const m = {
        tableBarChart: "TableBar",
        simpleBarChart: "SimpleBar",
        horizontalbarChart: "HorizontalBar",
        d3PieChart: "Pie",
        multiLineChart: "MultiLine",
      };
      return m[fn] || "MultiLine";
    },
    /**
     * LLM may wrap output (tool result, nested keys). Normalize to root with screen/config_version.
     */
    normalizeGenerateUiPayload(raw) {
      if (!raw || typeof raw !== "object") return raw;
      if (raw.screen || raw.screens || raw.config_version) return raw;
      const r = raw.result;
      if (r && typeof r === "object" && (r.screen || r.config_version)) return r;
      const keys = ["output", "menu_ui", "forewarn_viz_config", "data"];
      for (let i = 0; i < keys.length; i++) {
        const v = raw[keys[i]];
        if (v && typeof v === "object" && (v.screen || v.config_version)) return v;
      }
      return raw;
    },
    applyUiPayload(payload, sourceSqls = []) {
      const normalized = this.normalizeGenerateUiPayload(payload);
      if (!normalized || typeof normalized !== "object") return;

      const val = normalized.validation;
      if (val && Array.isArray(val.errors) && val.errors.length) {
        this.$bvToast.toast(val.errors.slice(0, 8).join("; "), {
          title: "LLM validation",
          variant: "danger",
          solid: true,
          autoHideDelay: 10000,
        });
      } else if (val && val.is_valid === false) {
        this.$bvToast.toast(
          "LLM marked the response as invalid — review Screen / Vizlet SQL and axes.",
          {
            title: "Validation",
            variant: "warning",
            solid: true,
            autoHideDelay: 8000,
          },
        );
      }
      if (val && Array.isArray(val.warnings) && val.warnings.length) {
        this.$bvToast.toast(val.warnings.slice(0, 5).join(" · "), {
          title: "Warnings",
          variant: "warning",
          solid: true,
          autoHideDelay: 7000,
        });
      }

      const clean = stripTenant(normalized);
      const screenRaw = clean.screen || (clean.screens && clean.screens[0]);
      if (!screenRaw) return;

      const screen = Object.assign(makeScreen(), screenRaw);
      if (!screen.screen_listbox) screen.screen_listbox = makeListbox();
      if (!screen.date_range_filter_config)
        screen.date_range_filter_config = makeDateRangeConfig();

      const rawScreenlets = Array.isArray(screenRaw.screenlets)
        ? screenRaw.screenlets
        : [];
      screen.screenlets = rawScreenlets.map((sl) => {
        const merged = Object.assign(makeScreenlet(), sl);
        const slb = merged.screenlet_listbox;
        if (
          !slb ||
          typeof slb !== "object" ||
          Object.keys(slb).length === 0
        ) {
          merged.screenlet_listbox = makeListbox();
        }
        if (!merged.date_range_filter_config)
          merged.date_range_filter_config = makeDateRangeConfig();

        const vizRaw = Array.isArray(sl.vizlets) ? sl.vizlets : [];
        merged.vizlets = vizRaw.map((vz, i) => {
          const v = Object.assign(makeVizlet(), vz);
          v.layout = Object.assign(makeVizletLayout(), vz.layout || {});
          v.axis = Object.assign(makeVizletAxis(), vz.axis || {});
          const src = sourceSqls[i];
          const dsTrim = String(v.datasql || "").trim();
          // Prefer user's source DataSQL when the LLM omitted it or returned a shortened
          // SELECT that drops GROUP BY / HAVING / ORDER BY (preview then matches SQLn fields).
          if (src && (!dsTrim || sourceSqlShouldReplaceVizletDatasql(src, dsTrim))) {
            v.datasql = src;
          }
          return v;
        });
        return merged;
      });

      // Replace (not append) to keep Generate UI deterministic per run.
      this.aiConfig = {
        config_version: clean.config_version || "2.0",
        screens: [screen],
      };
      this.selectNode({ type: "screen", si: 0 });
      this.$set(this.expanded, "sc-0", true);
      if (screen.screenlets.length) {
        this.$set(this.expanded, "sl-0-0", true);
      }
    },

    // =======================================================================
    // Menu master / legacy preview methods (unchanged behavior; retained for
    // the Step 3 "existing + new viz" card and existing postConfigMenu flow).
    // =======================================================================
    initMenuDefaults(menuId) {
      const uniqueParentMenuIdsMap = new Map();
      this.$store.state.menuMasterData.forEach((item) => {
        if (item.menuId !== null && item.menuId !== undefined) {
          uniqueParentMenuIdsMap.set(item.menuId, {
            id: item.menuId,
            name: item.menuName,
          });
        }
      });
      this.parentMenuIdOptions = Array.from(uniqueParentMenuIdsMap.values());

      const flatRow = this.$store.state.menuMasterFlat.find(
        (item) => item.menuId === menuId,
      );
      const parentId = flatRow ? flatRow.parentMenuId : null;
      const parentMenu = parentId
        ? this.$store.state.menuMasterData.find(
            (item) => item.menuId === parentId,
          )
        : null;
      this.defaultParentMenuId = parentMenu ? parentMenu.menuName : "";
      this.defaultMenuName = flatRow ? flatRow.menuName : "";

      this.vizTypeOptions = this.$store.state.vizType
        .map((item) => item.viztypeName)
        .sort();

      const screenRender = this.$store.state.fwConfigMenuScreenRender.find(
        (item) => item.menuId === menuId,
      );
      this.defaultFilterSyspk = screenRender
        ? screenRender.screenletListboxSyspkArray
        : "";
    },
    splitSelectColumns(selectPart) {
      const columns = [];
      let depth = 0;
      let current = "";
      for (const char of selectPart) {
        if (char === "(") depth++;
        if (char === ")") depth--;
        if (char === "," && depth === 0) {
          columns.push(current.trim());
          current = "";
        } else {
          current += char;
        }
      }
      if (current.trim()) columns.push(current.trim());
      return columns;
    },
    renderExistingChart() {
      let vizData = this.$store.state.fwConfigScreenElement.filter(
        (item) => item.menuId === this.activeMenuId,
      );
      if (!vizData || vizData.length === 0) {
        if (this.$refs.r1c1) this.$refs.r1c1.innerHTML = "";
        this.existingVizFunction = "";
        return;
      }
      if (this.$refs.r1c1) this.$refs.r1c1.innerHTML = "";

      let allVizType = this.$store.state.vizType;
      let filter = allVizType.filter(
        (t) => t.viztypeId === vizData[0].vizletId,
      )[0];
      if (!filter) {
        this.existingVizFunction = "";
        this.vizFunction = "";
        return;
      }
      let vizFunction = filter.viztypeFunctionName;
      this.existingVizFunction = vizFunction;
      this.vizFunction = vizFunction;

      const v = vizData[0];
      this.defaultChartData.x1D3ScaleType = v.x1_d3scaleType || "bandScale";
      this.defaultChartData.x2D3ScaleType = v.x2_d3scaleType || "bandScale";
      this.defaultChartData.y1D3ScaleType = v.y1_d3scaleType || "linearScale";
      this.defaultChartData.y2D3ScaleType = v.y2_d3scaleType || "linearScale";

      let margin = { top: 50, right: 150, bottom: 100, left: 80 };
      let vizunit = vizData[0].vizletSequence;
      let getChartInfo = (vu) => {
        let f = vizData[0];
        f.chartWidth = 800;
        f.chartHeight = 300;
        f.axisFontSize = 14;
        f.axisLabelFontSize = 12;
        if (f.length === 0) return new FwVizChartInfo(vu, margin, {}, false, []);
        return new FwVizChartInfo(vu, margin, f, vizData[0].visibleFlag, f);
      };
      var divId = this.$refs.r1c1;
      if (!divId) return;
      let chartData = getChartInfo(vizunit);
      var url =
        "/api/fetchDataWithParam/" +
        chartData.data.datasqlId +
        "/" +
        "CommonVizClass";
      const controller = apiAbort.new();
      axios
        .post(
          url,
          {},
          {
            signal: controller.signal,
            headers: {
              "info-params": JSON.stringify({
                menuId: this.$route.params.menuId,
                submenuId: this.$route.params.submenuId,
              }),
              "Content-Type": "application/json",
            },
          },
        )
        .then((resp) => {
          this.$commonFn.renderChartCompare(
            "CommonVizClass",
            chartData,
            divId,
            this.existingVizFunction,
            resp.data,
            {},
          );
        });
    },
    buildTableSQL() {
      const columns = this.selectedColumns.join(", ");
      return `
        SELECT ${columns}
        FROM ${this.selectedTable} LIMIT 100
      `;
    },
    syncTableBarPreviewRowsFromSql(sql) {
      if (!sql || !sql.trim()) {
        this.previewTableBarRows = [];
        return;
      }
      let ast;
      try {
        ast = sqlParser.astify(sql, { database: "PostgreSQL" });
      } catch (e) {
        this.previewTableBarRows = [];
        return;
      }
      if (Array.isArray(ast)) ast = ast[0];
      if (!ast || ast.type !== "select" || !Array.isArray(ast.columns)) {
        this.previewTableBarRows = [];
        return;
      }
      const existing = this.previewTableBarRows || [];
      /**
       * BI SELECTs typically mix raw dimension columns with aggregated measures
       * (SUM/COUNT/AVG/…). When a user has NOT yet touched the mapping UI we
       * must classify each column ourselves; otherwise every row defaults to
       * "Dimension" and a SUM(sales) column is rewritten as dim2_name instead
       * of metric1.
       *
       * Preference order for the default column type:
       *   1. previously-selected type (user override wins)
       *   2. an explicit metric dim*_name alias already in the SELECT
       *   3. AST aggregate detection (SUM, COUNT, window aggs, etc.)
       *   4. fall-through heuristic: if the SELECT has aggregates anywhere,
       *      treat remaining columns as dimensions; otherwise treat the last
       *      up-to-two columns as metrics (mirrors standardizeSelectAliases).
       */
      const aggFlags = ast.columns.map((col) => exprContainsAggregate(col.expr));
      const anyAggregate = aggFlags.some(Boolean);
      const autoAliasRe = /^(dim\d+_name|metric\d+)$/i;
      const newRows = ast.columns.map((col, idx) => {
        const prev = existing[idx] || {};
        const simpleSelectAst = {
          with: null,
          type: "select",
          options: null,
          distinct: null,
          columns: [col],
          into: { position: null },
          from: null,
          where: null,
          groupby: null,
          having: null,
          orderby: null,
          limit: null,
          locking_read: null,
          window: null,
          collate: null,
        };
        let expression = "";
        try {
          const sqlCol = sqlParser.sqlify(simpleSelectAst);
          expression = sqlCol.replace(/^select\s+/i, "").trim();
        } catch (e) {
          expression = "";
        }

        // Prefer a human alias for the label; skip auto-aliases like "metric1"
        // so the headerMap key later (label → alias) doesn't collide.
        let defaultLabel;
        if (prev.label != null) {
          defaultLabel = prev.label;
        } else if (col.as && !autoAliasRe.test(String(col.as))) {
          defaultLabel = col.as;
        } else {
          defaultLabel = expression;
        }

        // Resolve default type using the preference order above.
        let defaultType;
        if (prev.type === "Metric" || prev.type === "Dimension") {
          defaultType = prev.type;
        } else if (typeof col.as === "string" && /^metric\d*$/i.test(col.as)) {
          defaultType = "Metric";
        } else if (typeof col.as === "string" && /^dim\d*(_name)?$/i.test(col.as)) {
          defaultType = "Dimension";
        } else if (aggFlags[idx]) {
          defaultType = "Metric";
        } else if (
          !anyAggregate &&
          ast.columns.length >= 2 &&
          idx >= ast.columns.length - Math.min(2, ast.columns.length - 1)
        ) {
          defaultType = "Metric";
        } else {
          defaultType = "Dimension";
        }

        const prevGraph =
          typeof prev.graph === "string" ? prev.graph.toLowerCase() : "";
        const graphDefault = prevGraph.startsWith("y") ? "yes" : "no";
        return {
          required: `Col${idx + 1}`,
          expr: expression,
          label: defaultLabel,
          type: defaultType,
          graph: graphDefault,
        };
      });
      this.previewTableBarRows = newRows;
    },
    buildTableBarPreview(sql) {
      if (!sql) return { newSql: "", headerMap: {} };
      const chk = validateSelectListForAliasRewrite(sqlParser, sql);
      if (!chk.ok) {
        return {
          newSql: "",
          headerMap: {},
          error: chk.error,
          errorKind: chk.errorKind,
        };
      }
      const ast = JSON.parse(JSON.stringify(chk.ast));
      if (!Array.isArray(ast.columns)) return { newSql: sql, headerMap: {} };

      const rows = this.previewTableBarRows || [];
      let dimIndex = 1;
      let metricIndex = 1;
      const headerEntries = [];
      const newColumns = [];

      rows.forEach((row, idx) => {
        const colAst = ast.columns[idx] || null;
        const exprAst =
          colAst && colAst.expr
            ? colAst.expr
            : {
                type: "column_ref",
                table: null,
                column: row.expr,
                collate: null,
              };
        const label = row.label || row.expr;
        const isMetric =
          typeof row.type === "string" &&
          row.type.trim().toLowerCase() === "metric";
        const wantsGraph =
          isMetric &&
          typeof row.graph === "string" &&
          row.graph.trim().length > 0 &&
          row.graph.toLowerCase().startsWith("yes");
        let alias;
        let graphAlias = null;
        if (isMetric) {
          alias = `metric${metricIndex++}`;
          graphAlias = wantsGraph ? `graph${metricIndex - 1}` : null;
        } else {
          alias = `dim${dimIndex++}_name`;
        }
        newColumns.push({ expr: exprAst, as: alias });
        headerEntries.push({
          key: label,
          value: alias,
          graphKey: wantsGraph ? `${label}#` : null,
          graphValue: graphAlias,
        });
      });

      ast.columns = newColumns;
      let newSql;
      try {
        newSql = sqlParser.sqlify(ast, { database: "PostgreSQL" });
      } catch (e) {
        return {
          newSql: "",
          headerMap: {},
          error: e.message || String(e),
          errorKind: "syntax",
        };
      }
      const headerMap = {};
      headerEntries.forEach((e) => {
        headerMap[e.key] = e.value;
      });
      headerEntries.forEach((e) => {
        if (e.graphKey && e.graphValue) {
          headerMap[e.graphKey] = e.graphValue;
        }
      });
      return { newSql, headerMap };
    },
    standardizeSQL(sql) {
      return standardizeSelectAliases(sqlParser, sql);
    },
    appendDynamicClause(sql) {
      if (!sql) return "";
      const lowerSql = sql.toLowerCase();
      if (
        lowerSql.includes("fw_where_clause") ||
        lowerSql.includes("fw_and_clause")
      ) {
        return sql;
      }
      const hasWhere = /\bwhere\b/i.test(sql);
      const clause = hasWhere ? "fw_and_clause" : "fw_where_clause";
      let depth = 0;
      let splitIndex = -1;
      for (let i = 0; i < lowerSql.length; i++) {
        const ch = lowerSql[i];
        if (ch === "(") depth++;
        else if (ch === ")" && depth > 0) depth--;
        if (depth === 0) {
          if (
            lowerSql.startsWith("group by", i) ||
            lowerSql.startsWith("order by", i) ||
            lowerSql.startsWith("having", i) ||
            lowerSql.startsWith("limit", i)
          ) {
            splitIndex = i;
            break;
          }
        }
      }
      if (splitIndex !== -1) {
        return `${sql.slice(0, splitIndex)}\n${clause}\n${sql.slice(
          splitIndex,
        )}`;
      }
      return `${sql}\n${clause}`;
    },
    checkSqlClientRules(sql) {
      if (!sql || !sql.trim()) {
        return { ok: false, message: "SQL query cannot be empty" };
      }
      const trimmed = sql.trim();
      const sqlNoTrailingSemi = trimmed.replace(/;+\s*$/g, "");
      const forbiddenKeywords = [
        "update",
        "delete",
        "truncate",
        "drop",
        "alter",
        "insert",
        "create",
        "grant",
        "revoke",
      ];
      const lowerSql = sqlNoTrailingSemi.toLowerCase();
      if (lowerSql.includes(";")) {
        return { ok: false, message: "Multiple SQL statements are not allowed" };
      }
      for (let keyword of forbiddenKeywords) {
        const regex = new RegExp(`\\b${keyword}\\b`, "i");
        if (regex.test(sql)) {
          return {
            ok: false,
            message: `"${keyword.toUpperCase()}" operations are not allowed`,
          };
        }
      }
      if (!/^\s*(select|with)\b/i.test(sqlNoTrailingSemi)) {
        return { ok: false, message: "Only SELECT queries are allowed" };
      }
      return { ok: true };
    },
    validateSQL(sql) {
      const r = this.checkSqlClientRules(sql);
      if (r.ok) return true;
      this.showToast(r.message, "danger");
      return false;
    },
    showToast(message, variant = "danger") {
      this.$bvToast.toast(message, {
        title: "SQL Validation Error",
        variant: variant,
        solid: true,
        autoHideDelay: 4000,
      });
    },
    previewSqlRewriteToastTitle(errorKind) {
      switch (errorKind) {
        case "syntax":
          return "SQL syntax error";
        case "unsupported":
          return "SQL not supported for preview";
        case "validation":
          return "SQL validation";
        default:
          return "Preview SQL";
      }
    },
    toastIfPreviewRewriteError(result) {
      if (!result || !result.error) return false;
      this.$bvToast.toast(result.error, {
        title: this.previewSqlRewriteToastTitle(result.errorKind),
        variant: "warning",
        solid: true,
        autoHideDelay: 8000,
      });
      return true;
    },
    async generatePreview() {
      this.previewLoading = true;
      this.previewError = "";
      try {
        let sqlQuery = (this.dataSqlId || "").trim();
        if (!this.validateSQL(sqlQuery)) return;

        const vizFn = this.selectedPreviewVizType || "tableBarChart";
        let result;
        if (vizFn === "tableBarChart") {
          this.syncTableBarPreviewRowsFromSql(sqlQuery);
          await this.$nextTick();
          if (!this.previewTableBarRows.length) {
            this.$bvToast.toast(
              "Could not parse SELECT columns from your SQL. Fix the query and try again.",
              { title: "Preview", variant: "warning", solid: true },
            );
            return;
          }
          result = this.buildTableBarPreview(sqlQuery);
        } else {
          if (!this.isStandardPreviewSqlReady(sqlQuery)) {
            this.$bvToast.toast(
              "This visualization needs at least one dimension and one metric column in the SELECT.",
              { title: "Preview", variant: "warning", solid: true },
            );
            return;
          }
          result = this.standardizeSQL(sqlQuery);
          if (this.toastIfPreviewRewriteError(result)) return;
          if (!result.newSql || !String(result.newSql).trim()) {
            this.$bvToast.toast(
              "Could not normalize SQL for this chart type (need SELECT … FROM …).",
              { title: "Preview", variant: "warning", solid: true },
            );
            return;
          }
          this.defaultChartData.x1D3ScaleType = "bandScale";
          this.defaultChartData.y1D3ScaleType = "linearScale";
          this.defaultChartData.x1DataFieldName = "dim1_name";
          this.defaultChartData.y1DataFieldName = "metric1";
          this.syncDefaultChartAxisLabelsFromForm();
        }
        if (this.toastIfPreviewRewriteError(result)) return;

        sqlQuery = result.newSql;
        this.defaultChartData.reportHeaderColumnDp = result.headerMap;
        sqlQuery = this.appendDynamicClause(sqlQuery);
        this.defaultChartData.datasql = sqlQuery;
        if (this.selectedTable && this.selectedColumns.length) {
          sqlQuery = this.buildTableSQL();
        }

        const controller = apiAbort.new();
        const divId = this.$refs.r1c2;
        this.clearPreviewChartMount(divId);

        const data = { query: sqlQuery };
        const resp = await axios.post("/api/fetchData", data, {
          signal: controller.signal,
          skipGlobalDataErrorModal: true,
        });
        await this.$commonFn.renderChartCompare(
          "CommonVizClass",
          this.defaultChartData,
          divId,
          vizFn,
          resp.data,
          {},
        );
      } catch (e) {
        const info = classifyPreviewOrFetchError(e);
        this.previewError = info.message;
        // eslint-disable-next-line no-console
        console.error("generatePreview", e);
        this.$bvToast.toast(info.message, {
          title: info.title,
          variant: "danger",
          solid: true,
          autoHideDelay: 7000,
        });
      } finally {
        this.previewLoading = false;
      }
    },
    isStandardPreviewSqlReady(sql) {
      const trimmed = (sql || "").trim();
      const r = this.standardizeSQL(trimmed);
      if (r && !r.error && r.newSql && String(r.newSql).trim()) {
        const vals = Object.values(r.headerMap || {});
        const hasMetric = vals.some((v) => /^metric\d+$/i.test(String(v)));
        const hasDim = vals.some((v) => /^dim\d+_name$/i.test(String(v)));
        if (hasDim && hasMetric) return true;
      }
      const v = validateSelectListForAliasRewrite(sqlParser, trimmed);
      if (!v.ok) return false;
      const cols = v.ast.columns || [];
      if (cols.length < 2) return false;
      const flags = cols.map((c) => exprContainsAggregate(c.expr));
      let anyAgg = flags.some(Boolean);
      if (!anyAgg && cols.length >= 2) {
        const nMetric = Math.min(2, cols.length - 1);
        for (let i = cols.length - nMetric; i < cols.length; i++) {
          flags[i] = true;
        }
        anyAgg = true;
      }
      const hasNonAgg = flags.some((f) => !f);
      return anyAgg && hasNonAgg;
    },
    async onPreviewVizTypeChange() {
      const q = (this.dataSqlId || "").trim();
      this.syncDefaultChartAxisLabelsFromForm();
      if (!q) {
        this.$bvToast.toast("Enter a SQL query in the DataSQL field first.", {
          title: "Preview",
          variant: "info",
          solid: true,
        });
        return;
      }
      const rules = this.checkSqlClientRules(q);
      if (!rules.ok) {
        this.$bvToast.toast(rules.message || "Invalid SQL for preview.", {
          title: "Preview",
          variant: "warning",
          solid: true,
        });
        return;
      }
      if (!this.isPreviewEnabled) {
        this.$bvToast.toast(
          this.selectedPreviewVizType === "tableBarChart"
            ? "Could not parse SELECT columns for table + bar mapping."
            : "For this chart type the SELECT needs at least two columns (dimensions and/or metrics).",
          {
            title: "Preview",
            variant: "warning",
            solid: true,
            autoHideDelay: 8000,
          },
        );
        return;
      }
      if (
        this.lastAiMenuConfigPayload &&
        this.lastAiMenuConfigSql &&
        this.lastAiMenuConfigSql === q
      ) {
        const clone = JSON.parse(JSON.stringify(this.lastAiMenuConfigPayload));
        await this.applyAiMenuConfigPayload(clone, {
          skipFormTextFields: true,
          skipMetaToasts: true,
          skipPreview: true,
        });
      }
      await this.generatePreview();
    },
    clearPreviewChartMount(el) {
      if (!el) return;
      el.innerHTML = "";
      el.classList.remove("tbc-chart-scroll");
      el.removeAttribute("style");
    },

    // =======================================================================
    // Submit / reset — serialize aiConfig back into the LLM-shaped JSON.
    // =======================================================================
    submitForm() {
      this.$refs.observer.validate().then((valid) => {
        if (valid) {
          this.loadMenu = false;
          this.postConfigMenu();
        } else {
          this.$bvToast.toast("Please fill all required fields", {
            title: "Validation Error",
            variant: "danger",
            solid: true,
          });
        }
      });
    },
    buildPublishPayload() {
      const screen = this.aiConfig.screens[0] || null;
      return {
        config_version: this.aiConfig.config_version || "2.0",
        tenant: {
          menu_id: this.activeMenuId,
        },
        screen: screen ? stripTenant(screen) : null,
      };
    },
    /** Same chart key as the Step 3 preview dropdown (`selectedPreviewVizType`). */
    effectivePublishChartType(firstVizlet) {
      return (
        this.selectedPreviewVizType ||
        (firstVizlet && firstVizlet.viztype_function_name) ||
        "tableBarChart"
      );
    },
    /**
     * Resolve fw_m_viztype row by viztype_function_name (e.g. simpleBarChart, d3PieChart).
     * Submit must follow the preview chart selection, not stale LLM vizlet labels (fixes always V65).
     */
    resolveVizletTemplateFromChartFunctionName(chartFunctionName) {
      const fn = String(chartFunctionName || "").trim();
      const rows = this.$store.state.vizType || [];
      const t = rows.find(
        (x) => String(x.viztypeFunctionName || "").trim() === fn,
      );
      return {
        vizletId: t != null && t.viztypeId != null ? String(t.viztypeId) : "",
        viztypeName: t && t.viztypeName != null ? String(t.viztypeName) : "",
      };
    },
    /**
     * Same pipeline as generatePreview: rewrite SQL + header map for the selected chart type,
     * then insert fw_where_clause / fw_and_clause (server-side fetch contract).
     * @returns {{ ok: true, dataSql: string, tableHeadersJson: string, chartType: string } | { ok: false, message: string }}
     */
    buildPublishSqlAndReportHeaders(rawSql, firstVizlet, chartTypeOverride) {
      const trimmed = (rawSql || "").trim();
      if (!trimmed) {
        return { ok: false, message: "DataSQL is empty." };
      }
      const rules = this.checkSqlClientRules(trimmed);
      if (!rules.ok) {
        return { ok: false, message: rules.message || "Invalid SQL." };
      }
      const chartType =
        (chartTypeOverride != null && String(chartTypeOverride).trim() !== ""
          ? String(chartTypeOverride).trim()
          : null) ||
        this.selectedPreviewVizType ||
        (firstVizlet && firstVizlet.viztype_function_name) ||
        "tableBarChart";

      let rewrite;
      if (chartType === "tableBarChart") {
        this.syncTableBarPreviewRowsFromSql(trimmed);
        if (!this.previewTableBarRows.length) {
          return {
            ok: false,
            message:
              "Could not parse SELECT columns for Table + bar. Fix SQL or change the preview chart type.",
          };
        }
        rewrite = this.buildTableBarPreview(trimmed);
      } else {
        if (!this.isStandardPreviewSqlReady(trimmed)) {
          return {
            ok: false,
            message:
              "For this chart type the SELECT needs dimension/metric columns (see preview requirements).",
          };
        }
        rewrite = this.standardizeSQL(trimmed);
      }

      if (rewrite.error) {
        return { ok: false, message: rewrite.error };
      }
      if (!rewrite.newSql || !String(rewrite.newSql).trim()) {
        return {
          ok: false,
          message: "Could not normalize SQL for the selected chart type.",
        };
      }

      const dataSql = this.appendDynamicClause(String(rewrite.newSql).trim());
      const headerMap = rewrite.headerMap || {};
      return {
        ok: true,
        dataSql,
        tableHeadersJson: JSON.stringify(headerMap),
        chartType,
      };
    },
    postConfigMenu() {
      const nestedPayload = this.buildPublishPayload();

      const firstScreen = this.aiConfig.screens[0];
      const firstScreenlet = firstScreen && firstScreen.screenlets[0];
      const firstVizlet = firstScreenlet && firstScreenlet.vizlets[0];

      if (!firstVizlet || !String(firstVizlet.datasql || "").trim()) {
        this.$bvToast.toast(
          "Add a screen with at least one vizlet and DataSQL before submitting.",
          { title: "Validation Error", variant: "danger", solid: true },
        );
        this.loadMenu = true;
        return;
      }

      const rawDataSql = (
        firstVizlet.datasql ||
        this.dataSqlId ||
        this.defaultChartData.datasql ||
        ""
      ).trim();

      const publishChartType = this.effectivePublishChartType(firstVizlet);
      const vt = this.resolveVizletTemplateFromChartFunctionName(publishChartType);
      if (!vt.vizletId) {
        this.$bvToast.toast(
          `No viz template in Viz Master for chart "${publishChartType}". Use the preview chart dropdown to pick a registered type.`,
          { title: "Validation Error", variant: "danger", solid: true },
        );
        this.loadMenu = true;
        return;
      }

      const pub = this.buildPublishSqlAndReportHeaders(
        rawDataSql,
        firstVizlet,
        publishChartType,
      );
      if (!pub.ok) {
        this.$bvToast.toast(pub.message, {
          title: "Cannot publish",
          variant: "danger",
          solid: true,
          autoHideDelay: 9000,
        });
        this.loadMenu = true;
        return;
      }

      const existing = this.fetchDefaultvalues?.[0];
      const screenletLbSql =
        (firstScreenlet &&
          firstScreenlet.screenlet_listbox &&
          firstScreenlet.screenlet_listbox.listbox_sql) ||
        "";
      const formData = {
        /** Server pins parent to my_dashboard for fn_create_ai_menu (user dashboard subtree). */
        parentMenuId: "my_dashboard",
        menuName: this.menuName || firstScreenlet?.title || "",
        screenName: this.screenName || firstScreen?.screen_title || "",
        filterSqlId: this.filterSqlId,
        filterSyspk: this.filterSyspk,
        screenletListbox1SqlId: String(screenletLbSql || "").trim() || this.filterSqlId || "",
        dataSql: pub.dataSql,
        tableHeaders: pub.tableHeadersJson,
        previewChartType: pub.chartType,
        vizTypeName:
          vt.viztypeName ||
          existing?.viztypeName ||
          firstVizlet?.viztype_name ||
          this.vizTypeName,
        vizletId: vt.vizletId,
        x_axisName: firstVizlet?.axis?.x1_axis_display_name ?? this.x_axisName,
        y_axisName: firstVizlet?.axis?.y1_axis_display_name ?? this.y_axisName,
        vizTitleName: firstVizlet?.vizlet_title ?? this.vizTitleName,
        x_axisTooltip: firstVizlet?.axis?.x1_metric_tooltip ?? this.x_axisTooltip,
        y_axisTooltip: firstVizlet?.axis?.y1_metric_tooltip ?? this.y_axisTooltip,
        x_axisDataName: firstVizlet?.axis?.x1_data_field_name ?? this.x_axisDataName,
        y_axisDataName: firstVizlet?.axis?.y1_data_field_name ?? this.y_axisDataName,
        x1_d3scaleType:
          firstVizlet?.axis?.x1_d3scale_type ??
          existing?.x1_d3scaleType ??
          this.defaultChartData?.x1D3ScaleType ??
          "",
        aiConfig: nestedPayload,
      };

      axios
        .post("/api/sp-config-ai-menu", formData)
        .then((resp) => {
          if (resp.status === 200) {
            const mid = resp.data && resp.data.menuId;
            this.$bvToast.toast(
              mid
                ? `Menu saved under My Dashboard. New menu id: ${mid}.`
                : "Menu has been published to My Dashboard.",
              { title: "Success", variant: "success", solid: true },
            );
            this.loadMenu = true;
            this.resetForm();
            setTimeout(() => {
              window.location.reload();
            }, 5000);
          }
        })
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.error("sp-config-ai-menu", e);
          this.$bvToast.toast(
            "Could not save the menu. Check server logs for details.",
            { title: "Failed to Save Menu", variant: "danger", solid: true },
          );
        });
    },
    resetForm() {
      this.parentMenuDisplay = "my_dashboard";
      this.menuId = "";
      this.menuName = "";
      this.screenName = "";
      this.filterSqlId = "";
      this.filterSyspk = "";
      this.dataSqlId = "";
      this.vizTypeName = "";
      this.x_axisName = "";
      this.y_axisName = "";
      this.vizTitleName = "";
      this.x_axisTooltip = "";
      this.y_axisTooltip = "";
      this.x_axisDataName = "";
      this.y_axisDataName = "";
      this.tableHeaders = [];
      this.lastAiMenuConfigPayload = null;
      this.lastAiMenuConfigSql = "";
      this.datasqls = [""];
      this.generateUiUserPrompt = "";
      this.aiConfig = { config_version: "2.0", screens: [] };
      this.selectedNode = { type: null, si: 0, sli: 0, vi: 0 };
      this.expanded = {};
      this.currentStep = 1;
    },
    addField() {
      this.tableHeaders.push({
        id: this.nextFieldId++,
        header: "",
        value: "",
      });
    },
    removeField(index) {
      this.tableHeaders.splice(index, 1);
    },
    resolveParentMenuObject() {
      const q = (this.parentMenuDisplay || "").trim();
      if (!q) return null;
      const list = this.$store.state.menuMasterData || [];
      let m = list.find((x) => x.menuId === q || x.menuName === q);
      if (!m) {
        const ql = q.toLowerCase();
        m = list.find(
          (x) =>
            (x.menuName && String(x.menuName).toLowerCase() === ql) ||
            (x.menuId && String(x.menuId).toLowerCase() === ql),
        );
      }
      return m ? { id: m.menuId, name: m.menuName } : null;
    },
    syncDefaultChartAxisLabelsFromForm() {
      const sx = (this.x_axisName || "").trim();
      const sy = (this.y_axisName || "").trim();
      if (sx) this.defaultChartData.x1AxisDisplayName = sx;
      if (sy) this.defaultChartData.y1AxisDisplayName = sy;
      const tx = (this.x_axisTooltip || "").trim();
      const ty = (this.y_axisTooltip || "").trim();
      if (tx) this.defaultChartData.x1MetricTooltip = tx;
      if (ty) this.defaultChartData.y1MetricTooltip = ty;
    },
    isAiTableBarChartMappings(data) {
      const cm = data && data.chart_mappings;
      if (!Array.isArray(cm) || !cm.length) return false;
      const first = cm[0];
      return !!(first && typeof first === "object" && first.required != null);
    },
    applyAiChartMappings(rows) {
      if (!Array.isArray(rows) || !this.previewTableBarRows.length) return;
      const normType = (t) =>
        String(t || "")
          .toLowerCase()
          .startsWith("met")
          ? "Metric"
          : "Dimension";
      const normGraph = (g) =>
        String(g || "")
          .toLowerCase()
          .startsWith("y")
          ? "yes"
          : "no";
      rows.forEach((r, i) => {
        let pr = this.previewTableBarRows[i];
        const req = r && r.required != null ? String(r.required).trim() : "";
        if (req && /^col\d+$/i.test(req)) {
          const n = parseInt(req.replace(/^col/i, ""), 10);
          if (!Number.isNaN(n) && this.previewTableBarRows[n - 1]) {
            pr = this.previewTableBarRows[n - 1];
          }
        }
        if (!pr) return;
        if (r.column != null && String(r.column).trim()) {
          pr.label = String(r.column).trim();
        }
        pr.type = normType(r.type);
        pr.graph = normGraph(r.graph);
      });
    },
    async applyAiMenuConfigPayload(data, opts = {}) {
      if (!data || typeof data !== "object") return;
      const persistCache = opts.persistCache === true;
      const skipFormTextFields = opts.skipFormTextFields === true;
      const skipMetaToasts = opts.skipMetaToasts === true;
      const skipPreview = opts.skipPreview === true;

      const incomingSql = (this.dataSqlId || "").trim();
      let finalSql = incomingSql;

      if (!skipFormTextFields) {
        if (data.sql != null && String(data.sql).trim()) {
          const candidate = String(data.sql).trim();
          const check = this.checkSqlClientRules(candidate);
          if (check.ok) {
            this.dataSqlId = candidate;
            finalSql = candidate;
          } else {
            this.$bvToast.toast(
              `AI returned SQL that failed validation (${check.message}). Keeping your current query.`,
              { title: "AI", variant: "warning", solid: true, autoHideDelay: 6000 },
            );
          }
        }
        if (data.parent_menu) this.parentMenuDisplay = String(data.parent_menu);
        if (data.menu_name) this.menuName = String(data.menu_name);
        if (data.screen_title) this.screenName = String(data.screen_title);
        if (data.x_axis_name) this.x_axisName = String(data.x_axis_name);
        if (data.y_axis_name) this.y_axisName = String(data.y_axis_name);
        if (data.x_axis_tooltip) this.x_axisTooltip = String(data.x_axis_tooltip);
        if (data.y_axis_tooltip) this.y_axisTooltip = String(data.y_axis_tooltip);

        // Mirror into the currently-selected vizlet (Step 3 friendliness).
        if (this.selectedVizlet) {
          const v = this.selectedVizlet;
          if (data.screen_title) v.vizlet_title = String(data.screen_title);
          if (data.x_axis_name) v.axis.x1_axis_display_name = String(data.x_axis_name);
          if (data.y_axis_name) v.axis.y1_axis_display_name = String(data.y_axis_name);
          if (data.x_axis_tooltip) v.axis.x1_metric_tooltip = String(data.x_axis_tooltip);
          if (data.y_axis_tooltip) v.axis.y1_metric_tooltip = String(data.y_axis_tooltip);
          if (finalSql) v.datasql = finalSql;
        }
      }

      await this.$nextTick();
      const useTableBarSync =
        this.selectedPreviewVizType === "tableBarChart" &&
        this.isAiTableBarChartMappings(data);
      if (useTableBarSync) {
        this.syncTableBarPreviewRowsFromSql(finalSql);
        await this.$nextTick();
        this.applyAiChartMappings(data.chart_mappings || []);
      }

      await this.$nextTick();
      this.syncDefaultChartAxisLabelsFromForm();

      if (!skipMetaToasts) {
        const summary = data.analysis_summary;
        if (summary && String(summary).trim()) {
          const text = String(summary).trim();
          this.$bvToast.toast(text.length > 600 ? `${text.slice(0, 600)}…` : text, {
            title: "AI analysis",
            variant: "info",
            solid: true,
            autoHideDelay: 10000,
          });
        }
        const viz = data.viz_suggestions;
        if (Array.isArray(viz) && viz.length) {
          this.$bvToast.toast(viz.slice(0, 4).join(" · "), {
            title: "Viz suggestions",
            variant: "secondary",
            solid: true,
            autoHideDelay: 8000,
          });
        }
      }

      await this.$nextTick();
      if (!skipPreview && this.isPreviewEnabled) {
        await this.generatePreview();
      }
      if (persistCache) {
        this.lastAiMenuConfigPayload = JSON.parse(JSON.stringify(data));
        this.lastAiMenuConfigSql = (this.dataSqlId || "").trim();
      }
    },
    async generateWithAi() {
      this.aiGenerateError = "";
      const sql = (this.dataSqlId || "").trim();
      if (!sql) return;
      if (!this.validateSQL(sql)) return;

      this.aiGenerateLoading = true;
      try {
        const { data } = await generateMenuConfigFromSql(sql, {
          vizTypeFunction: this.selectedPreviewVizType || "",
          vizParams: this.previewVizParamsFromMaster,
        });
        await this.applyAiMenuConfigPayload(data, { persistCache: true });
        this.$bvToast.toast(
          "Form updated from AI. Changing the preview chart reuses this analysis—no second AI call while the SQL query is unchanged.",
          {
            title: "AI",
            variant: "success",
            solid: true,
            autoHideDelay: 5000,
          },
        );
      } catch (e) {
        const d = e.response && e.response.data;
        this.aiGenerateError =
          (d && d.error) ||
          (typeof d === "string" ? d : null) ||
          e.message ||
          "AI generation failed";
        this.$bvToast.toast(this.aiGenerateError, {
          title: "AI",
          variant: "danger",
          solid: true,
          autoHideDelay: 6000,
        });
      } finally {
        this.aiGenerateLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ================== Root / page ================== */
.menu-page-scroll {
  max-height: calc(100vh - 80px);
  overflow: auto;
  padding: 20px;
  width: 100%;
}
.ai-menu-config-root {
  position: relative;
}

/* ================== SQL entry card ================== */
.ai-sql-entry-card .ai-sql-row {
  display: grid;
  grid-template-columns: 54px 1fr 36px;
  align-items: start;
  gap: 8px;
  margin-bottom: 6px;
}
.ai-sql-row-label {
  font-size: 12px;
  font-weight: 600;
  color: #495057;
  padding-top: 6px;
}
.ai-sql-row-textarea {
  font-size: 12px;
  font-family: "Consolas", "Monaco", monospace;
}
.ai-sql-row-remove {
  width: 32px;
  height: 32px;
  padding: 0;
  line-height: 1;
}
.ai-sql-entry-actions {
  display: flex;
  align-items: center;
}
.btn-generate-ui {
  font-weight: 600;
}

/* ================== 3-Step progress stepper ================== */
.ai-step-progress {
  display: flex;
  gap: 6px;
  margin: 0 0 16px;
  padding: 8px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
}
.ai-step {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #dee2e6;
  background: #fff;
  border-radius: 4px;
  font-weight: 500;
  color: #495057;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}
.ai-step:hover:not(:disabled) {
  background: #eef3fb;
  border-color: #b6cdf2;
}
.ai-step-num {
  display: inline-flex;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #dee2e6;
  color: #495057;
  font-size: 12px;
  font-weight: 700;
  align-items: center;
  justify-content: center;
}
.ai-step-active {
  border-color: #4285f4;
  background: #e8f0fe;
  color: #1a56db;
}
.ai-step-active .ai-step-num {
  background: #4285f4;
  color: #fff;
}
.ai-step-done {
  border-color: #28a745;
  color: #155724;
}
.ai-step-done .ai-step-num {
  background: #28a745;
  color: #fff;
}
.ai-step-disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* ================== Workspace: tree + form ================== */
.ai-workspace {
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 16px;
  align-items: flex-start;
}
.ai-tree-panel {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 10px 8px;
  max-height: calc(100vh - 260px);
  overflow: auto;
  position: sticky;
  top: 0;
}
.ai-tree-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  padding: 4px 4px 8px;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 6px;
}
.ai-tree-root-label {
  font-weight: 700;
  color: #343a40;
}
.ai-tree-add-root {
  font-size: 11px;
  padding: 2px 8px;
  line-height: 1.5;
}
.ai-tree,
.ai-tree-children {
  list-style: none;
  margin: 0;
  padding: 0;
}
.ai-tree-children {
  padding-left: 16px;
  border-left: 1px dashed #e0e0e0;
  margin-left: 8px;
}
.ai-tree-node {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 6px;
  border-radius: 4px;
  cursor: pointer;
  color: #343a40;
  font-size: 13px;
  transition: background 0.12s;
}
.ai-tree-node:hover {
  background: #f1f3f5;
}
.ai-tree-node-selected {
  background: #e8f0fe;
  color: #1a56db;
  font-weight: 600;
}
.ai-tree-caret,
.ai-tree-caret-placeholder {
  width: 14px;
  display: inline-block;
  text-align: center;
  font-size: 11px;
  color: #868e96;
}
.ai-tree-node-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ai-tree-actions {
  display: inline-flex;
  align-items: center;
  opacity: 0;
  transition: opacity 0.15s;
}
.ai-tree-node:hover .ai-tree-actions,
.ai-tree-node-selected .ai-tree-actions {
  opacity: 1;
}
.ai-tree-action-btn {
  padding: 0 6px;
  font-size: 14px;
  line-height: 1;
  color: #4285f4;
  text-decoration: none;
}
.ai-tree-action-btn:hover {
  text-decoration: none;
  color: #1a56db;
}
.ai-tree-remove-btn {
  color: #dc3545;
}
.ai-tree-remove-btn:hover {
  color: #a71d2a;
}
.ai-tree-inline-add {
  padding: 3px 8px;
  color: #4285f4;
  font-size: 12px;
  cursor: pointer;
  border-radius: 3px;
}
.ai-tree-inline-add:hover {
  background: #e8f0fe;
}
.ai-tree-empty {
  padding: 10px 6px;
}

/* ================== Right form panel ================== */
.ai-form-panel {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 14px 16px;
  min-height: 400px;
}
.ai-form-empty {
  padding: 40px 20px;
  text-align: center;
}
.ai-form-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 14px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e9ecef;
}
.ai-form-breadcrumb {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 60%;
}

/* ================== Accordion (vizlet) ================== */
.ai-acc {
  border: 1px solid #e9ecef;
  margin-bottom: 8px;
  border-radius: 4px;
  overflow: hidden;
}
.ai-acc-btn {
  text-align: left;
  padding: 10px 14px;
  font-weight: 600;
  color: #343a40;
  background: #f8f9fa;
  border: none;
  border-radius: 0;
  text-decoration: none;
}
.ai-acc-btn:hover,
.ai-acc-btn:focus {
  background: #eef3fb;
  text-decoration: none;
  color: #1a56db;
}

/* ================== Preview card (existing, retained) ================== */
.preview-card,
.preview-card .card-body {
  padding: 5px;
}
.preview-charts {
  display: flex;
  gap: 20px;
}
.chart-box {
  flex: 1;
  min-height: 350px;
  min-width: 300px;
  border: 1px solid #e0e0e0;
  padding: 10px;
}
.preview-chart-header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}
.preview-viz-select-wrap {
  flex: 1 1 200px;
  max-width: 320px;
  min-width: 160px;
}
.preview-viz-label {
  display: block;
  font-size: 11px;
  color: #6c757d;
  margin-bottom: 4px;
  font-weight: 600;
}
.preview-viz-select-control {
  width: 100%;
}
.preview-chart-mount {
  position: relative;
  min-height: 120px;
}
.preview-chart-loading {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.85);
}
.preview-chart-canvas-dim {
  opacity: 0.35;
  pointer-events: none;
}
@media (min-width: 769px) and (max-width: 1300px) {
  .chart-box {
    min-height: 100px;
  }
}
.scrollmenu {
  max-height: 300px;
  overflow: auto;
}
.mapping-table code {
  font-size: 0.85em;
  color: #495057;
  background: #f8f9fa;
  padding: 2px 5px;
  border-radius: 3px;
}
.preview-col-wrap {
  min-width: 0;
  overflow: hidden;
}
.preview-sql-row .sql-form-group {
  margin-bottom: 0.5rem;
}
.preview-sql-row .sql-form-group >>> textarea,
.preview-card .sql-textarea {
  max-height: 120px;
  font-size: 12px;
  padding: 6px 8px;
  width: 100%;
  box-sizing: border-box;
}
.mapping-table-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  max-width: 100%;
}
.mapping-table-wrap .mapping-table {
  min-width: 200px;
  font-size: 13px;
}
.mapping-table-wrap .mapping-table th,
.mapping-table-wrap .mapping-table td {
  padding: 4px 6px;
  white-space: nowrap;
}
.mapping-table-wrap .mapping-table td:last-child {
  white-space: normal;
  word-break: break-word;
  max-width: 180px;
}
@media (max-width: 1400px) {
  .preview-sql-row .sql-form-group >>> textarea,
  .preview-card .sql-textarea {
    max-height: 90px;
    font-size: 11px;
    padding: 4px 6px;
  }
  .mapping-table-wrap .mapping-table {
    font-size: 12px;
  }
  .mapping-table-wrap .mapping-table th,
  .mapping-table-wrap .mapping-table td {
    padding: 3px 5px;
  }
  .mapping-table-wrap .mapping-table td:last-child {
    max-width: 140px;
  }
}
@media (max-width: 992px) {
  .preview-sql-row .sql-form-group >>> textarea,
  .preview-card .sql-textarea {
    max-height: 70px;
    font-size: 11px;
  }
  .mapping-table-wrap .mapping-table {
    font-size: 11px;
  }
  .mapping-table-wrap .mapping-table th,
  .mapping-table-wrap .mapping-table td {
    padding: 2px 4px;
  }
  .mapping-table-wrap .mapping-table td:last-child {
    max-width: 120px;
  }
}
@media (max-width: 768px) {
  .ai-workspace {
    grid-template-columns: 1fr;
  }
  .ai-tree-panel {
    position: static;
    max-height: 280px;
  }
  .preview-sql-row .sql-form-group >>> textarea,
  .preview-card .sql-textarea {
    max-height: 60px;
    font-size: 10px;
    padding: 3px 5px;
  }
  .preview-mapping-row .mapping-table-wrap {
    margin-bottom: 0.5rem;
  }
  .mapping-table-wrap .mapping-table td:last-child {
    max-width: 100%;
  }
}

.inline-mapping-input {
  font-family: "Consolas", "Monaco", "Courier New", monospace;
  font-size: 10px;
  color: #495057;
  background: #f8f9fa;
  border: 1px solid transparent;
  border-radius: 3px;
  padding: 1px 5px;
  width: 100%;
  outline: none;
  transition: border-color 0.15s, background 0.15s;
}
.inline-mapping-input:focus {
  background: #fff;
  border-color: #80bdff;
}
.type-select.custom-select {
  font-family: "Consolas", "Monaco", "Courier New", monospace;
  font-size: 10px;
  color: #495057;
  background-color: #f8f9fa;
  border-radius: 3px;
  padding: 2px 5px;
  width: 100%;
  height: calc(1.5em);
  line-height: 1;
}
.inline-mapping-input::placeholder {
  color: #adb5bd;
  font-style: italic;
}

.preview-actions-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 0;
  line-height: 1;
  margin-top: -20px;
}
.preview-actions-left {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
}
.preview-actions-right {
  flex-shrink: 0;
}
.btn-generate-ai {
  font-size: 14px;
}
.ai-generate-error {
  max-width: min(100%, 420px);
}

/* ================== Gemini chat trigger ================== */
.ai-menu-config-top-bar {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  padding-left: 2px;
}
.ai-menu-gemini-btn {
  line-height: 0;
  border: none;
  border-radius: 10px;
  flex-shrink: 0;
  padding: 4px !important;
  transition: background 0.15s ease, box-shadow 0.15s ease;
}
.ai-menu-gemini-btn:hover {
  background: rgba(66, 133, 244, 0.09);
}
.ai-menu-gemini-btn:focus {
  box-shadow: 0 0 0 2px rgba(66, 133, 244, 0.25);
}
.gemini-brand-svg {
  display: block;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.12));
}

.ai-menu-llm-sidebar-body {
  height: calc(100vh - 56px);
  overflow: auto;
  padding: 0 8px 16px;
}

/* ================== Footer ================== */
.ai-footer-actions {
  padding: 12px 0 8px;
  border-top: 1px solid #e9ecef;
  margin-top: 16px !important;
}
</style>
